import { z } from 'zod';
import { AccentBlockSchema, AppThemeMapSchema, DEFAULT_APP_THEMES } from './theme';
import { PerfTrendsConfigSchema } from './perf-trends';
import { MonitorsAdminConfigSchema } from './monitors';
import { SalesforceAdminConfigSchema } from './salesforce-presets';
import { ContractTestingAdminConfigSchema } from './contract-testing';
import { VisualAiConfigSchema } from './visual-ai';
import { ScreenshotRedactionConfigSchema } from './screenshot-redaction';
import { RegionIdSchema } from './regions';

/**
 * Batch 3 PR #2 — per-deployment region identity.
 *
 * `currentRegion` declares which Rynora region the cluster running
 * this API instance physically serves. In v0 there is exactly one
 * cluster (`us-phx`); future EU / AP clusters override this in
 * their own admin_config row.
 *
 * `availableRegions` is the public-facing roster the GET /api/regions
 * endpoint advertises. It's a deliberate operator switch (not derived
 * from the `RegionStatus` field in the catalogue) so we can dark-launch
 * a region — turn it on in the cluster, hold off the trust page
 * promotion until we're confident — or pull a region from the roster
 * without a code change in an incident.
 */
export const RegionsConfigSchema = z.object({
  currentRegion: RegionIdSchema,
  availableRegions: z.array(RegionIdSchema).min(1),
});

// `defer-pickers-oauth-flow` — admin-tunable credentials for the
// Gmail / Outlook integration connect dance. Off by default; the
// connect modal surfaces "ask your admin to enable this" when the
// client ids are still empty so operators don't accidentally pop
// open a half-broken OAuth dance for QA users.
export const ProviderOAuthConfigSchema = z.object({
  enabled: z.boolean().default(false),
  gmailClientId: z.string().default(''),
  gmailClientSecret: z.string().default(''),
  outlookClientId: z.string().default(''),
  outlookClientSecret: z.string().default(''),
  outlookTenant: z.string().default('common'),
  /** Operator-controlled redirect URL; the API uses this for the OAuth dance. */
  redirectUrl: z.string().default(''),
});
export type ProviderOAuthConfig = z.infer<typeof ProviderOAuthConfigSchema>;

// `defer-templates-user-saved` + `defer-templates-ai-generated` —
// admin-tunable governance for the Template gallery's two
// long-deferred entry points. Both default to ON for user-saved
// templates (so teams can save without an admin flip) but the AI
// generator is off by default — an operator opts in once a
// provider is configured, the same model we use for the
// contrast-advisor AI fix endpoint.
export const FlowTemplatesConfigSchema = z.object({
  /** Master kill switch — disables both routes when false. */
  enabled: z.boolean().default(true),
  /** "Generate with AI" tab visibility on the gallery. */
  generatorEnabled: z.boolean().default(false),
  /**
   * Per-org ceiling on persisted templates. Set to 0 to disable
   * the cap. Stops a runaway "Save as template" macro from
   * filling the table.
   */
  maxPerOrg: z.number().int().nonnegative().max(1000).default(200),
  /**
   * Per-feature AI provider override for the generator endpoint.
   * Empty string falls back to the default `intentProvider`.
   */
  generatorProvider: z.string().default(''),
});
export type FlowTemplatesConfig = z.infer<typeof FlowTemplatesConfigSchema>;

// `pending-rynora-agent` (migration 114) — admin-tunable governance
// for the Rynora Agent v0 loop. Default OFF so a fresh install never
// runs the discovery worker until an operator opts in by flipping
// `enabled` to true. Per-org enablement / budget are v1 — v0 is a
// single global switch + a per-day draft cap to keep back-pressure
// bounded if the configured URL list is large.
export const AgentConfigSchema = z.object({
  /**
   * Master kill switch. When false, the rynora-agent worker skips
   * the entire tick. Default off so existing deployments behave
   * exactly as before this migration until an operator opts in.
   */
  enabled: z.boolean().default(false),
  /**
   * The UTC hour the worker runs at. Mirrors the same field on
   * `security.dast` and `perfTrends.worker.runHourUtc` so operators
   * can stagger nightly workers and avoid one tick stomping the
   * other's DB cycles. 3am UTC is the default because it's outside
   * peak request hours in every supported region.
   */
  runHourUtc: z.number().int().min(0).max(23).default(3),
  /**
   * Per-day cap on findings the agent will actually draft a stub
   * test case for. If `crawl_root_urls` contains 200 entries and 195
   * are uncovered, only the first `maxDraftsPerDay` (sorted by URL,
   * deterministic) become drafts on day 1; the surplus stays as
   * `status='open'` and surfaces in the next day's digest. Stops a
   * misconfigured root-URL list from filling the `test_cases` table
   * with stubs overnight.
   */
  maxDraftsPerDay: z.number().int().nonnegative().max(100).default(5),
  /**
   * In-app deeplink fragment the daily digest's "review" button
   * targets. Lives in admin_config so the flow-builder app can
   * relocate the agent review page without forcing a worker code
   * change. Should resolve to the FlowBuilder route that lists open
   * + drafted agent_findings. Empty string disables the deeplink
   * (digest renders text-only).
   */
  reviewDeeplinkFragment: z.string().default('#/agent/findings'),
  /**
   * `pending-rynora-agent-v1` Session 2 / Chat B — interactive
   * Slack receiver block. Each install registers a single Slack app
   * (one app per Rynora install in v1, not per-org — Slack
   * Marketplace listing is v2). Operators paste the signing-secret
   * + bot-token from the Slack app's "Basic Information" / "OAuth
   * & Permissions" pages. The receiver verifies every inbound
   * request's `x-slack-signature` against `signingSecret` and a
   * 5-minute timestamp window.
   *
   * Stored as plaintext in `admin_config.config` here because the
   * runtime accessor (`getEffectiveSlackCreds`) wraps the read in
   * a crypto-vault decrypt step — the WRITER (`PUT /api/admin/config`)
   * encrypts on write so the at-rest copy in the JSONB column is
   * already ciphertext. Same posture as `jiraApiKey` /
   * `githubAppPrivateKey`.
   */
  slack: z
    .object({
      enabled: z.boolean().default(false),
      signingSecret: z.string().default(''),
      botToken: z.string().default(''),
      /**
       * Default Slack channel id (`C…` for public, `G…` for
       * private group). Daily digests may fall back here when an
       * org has not pinned `agent_org_config.slack_default_channel`.
       * Slack `/approve` org binding (Phase 8.21.9) does **not** use
       * this admin default — each org must pin its channel via
       * Usage → Agent (or PUT agent-config) so channel → org is unique.
       */
      defaultChannel: z.string().default(''),
    })
    .default({ enabled: false, signingSecret: '', botToken: '', defaultChannel: '' }),
  /**
   * `pending-ai-exploratory-tester` Session 1 (2026-05-30) — admin
   * knobs for the exploratory pass ("monkey with taste"). v0
   * defaults are intentionally DISABLED (`enabled: false`); operators
   * opt each install in explicitly. Per-org overrides for the 3
   * most-tunable knobs (`enabled` / `wallClockMinutes` /
   * `maxFindingsPerDay`) live on `agent_org_config` (extended in
   * migration 130). The remaining knobs (`ignoreSelectors`,
   * `formDefaults`, `axeProfile`) stay admin-config-only.
   *
   * Locked design-doc decisions #11–16 (see
   * `docs/handbook/features/pending/ai-exploratory-tester.md` §
   * "Session 1 additional locked decisions").
   */
  exploratory: z
    .object({
      /** Master kill switch for the exploratory pass. Defaults OFF. */
      enabled: z.boolean().default(false),
      /**
       * UTC hour the exploratory pass runs at. Defaults to 2 — one
       * hour AHEAD of the agent's default `runHourUtc=3` so a slow
       * exploratory run doesn't push the digest off-schedule.
       */
      runHourUtc: z.number().int().min(0).max(23).default(2),
      /** Wall-clock budget per project per day, in minutes. */
      wallClockMinutes: z.number().int().min(5).max(120).default(30),
      /**
       * Daily findings cap per (org, project). Dedup-respected — a
       * recurring console.error doesn't burn the budget more than
       * once per (url, subKind, elementSelector).
       */
      maxFindingsPerDay: z.number().int().nonnegative().max(500).default(20),
      /**
       * axe-core profile to use for `axe_violation` detection. v0
       * locks `'wcag2aa'`; `'wcag2a'` available for lighter
       * surfaces (marketing pages) and `'wcag21aa'` for the
       * stricter 2.1 ruleset. Session 2 wires this into the
       * runner-side `@axe-core/playwright` call.
       */
      axeProfile: z.enum(['wcag2a', 'wcag2aa', 'wcag21aa']).default('wcag2aa'),
      /**
       * CSS selectors to skip during exploration. Combined with the
       * per-element `data-rynora-exploratory-skip` attribute
       * (locked design-doc decision #6). Selectors are space-joined
       * before being passed to `document.querySelectorAll` — empty
       * list disables the per-config skip layer (the attribute
       * layer always applies).
       */
      ignoreSelectors: z.array(z.string()).default([]),
      /**
       * Safe-default form-fill values keyed by HTML5 input type.
       * Locked design-doc decision #7. Operators override one or
       * more keys without losing the defaults for the rest. v0 keys
       * locked: `email` / `tel` / `number` / `text` / `url` /
       * `password` (always skipped regardless of value) /
       * `search`.
       */
      formDefaults: z
        .object({
          email: z.string().default('dummy@test.example'),
          tel: z.string().default('+15555550100'),
          number: z.string().default('1'),
          text: z.string().default('Rynora exploratory tester'),
          url: z.string().default('https://example.com'),
          search: z.string().default('rynora'),
        })
        .default({
          email: 'dummy@test.example',
          tel: '+15555550100',
          number: '1',
          text: 'Rynora exploratory tester',
          url: 'https://example.com',
          search: 'rynora',
        }),
    })
    .default({
      enabled: false,
      runHourUtc: 2,
      wallClockMinutes: 30,
      maxFindingsPerDay: 20,
      axeProfile: 'wcag2aa',
      ignoreSelectors: [],
      formDefaults: {
        email: 'dummy@test.example',
        tel: '+15555550100',
        number: '1',
        text: 'Rynora exploratory tester',
        url: 'https://example.com',
        search: 'rynora',
      },
    }),
});
export type AgentConfig = z.infer<typeof AgentConfigSchema>;
export type AgentSlackConfig = AgentConfig['slack'];
export type AgentExploratoryConfig = AgentConfig['exploratory'];

// `pending-prod-feedback-loop` v0 (migration 115) — operator-tunable
// production-telemetry connectors. v0 only ships the Sentry sub-block;
// v1 adds analytics + RUM sources (Mixpanel / PostHog / Amplitude /
// GA4 / Datadog RUM / New Relic Browser) as siblings under
// `prodFeedback.*` mirroring the AI per-provider shape (one
// credential block per source). Each block defaults OFF so a fresh
// install never tries to poll an unconfigured upstream.
export const ProdFeedbackSentryConfigSchema = z.object({
  /**
   * Master switch for the Sentry connector. When false, the
   * prod-error-ingest worker short-circuits the Sentry source on
   * every tick. Default off so a fresh install never tries to
   * authenticate against api.sentry.io until an operator pastes
   * `authToken` + populates `orgSlug` / `projectSlug` /
   * `targetProjectId`.
   */
  enabled: z.boolean().default(false),
  /**
   * Sentry organisation slug — the `<org_slug>` segment in
   * `https://sentry.io/api/0/projects/<org_slug>/<project_slug>/issues/`.
   * Visible on Sentry's Settings → General page.
   */
  orgSlug: z.string().default(''),
  /**
   * Sentry project slug — the `<project_slug>` segment in the same
   * URL. One Rynora env can only target one Sentry project in v0;
   * per-Rynora-org multi-project routing lands in v1.
   */
  projectSlug: z.string().default(''),
  /**
   * Sentry Personal Access Token (PAT) with `project:read` +
   * `event:read` scopes. Sent as `Authorization: Bearer …`.
   * Encrypted via crypto-vault on write; redacted on read. Empty
   * string disables the connector regardless of `enabled`.
   */
  authToken: z.string().default(''),
  /**
   * The Rynora project that ingested findings get filed under. UUID
   * pointing at a `projects.id` row in the operator's primary org.
   * Required — the worker skips orgs whose `targetProjectId` is
   * empty / not a real project. v1 swaps this for per-org routing
   * via a sentry-projects-to-rynora-projects mapping table.
   */
  targetProjectId: z.string().default(''),
  /**
   * Per-day cap on the number of distinct Sentry issues the worker
   * will absorb into `agent_findings` per source. Re-occurrences of
   * an already-recorded issue do NOT count against this cap (they
   * upsert an existing row). Default 50 — high enough to absorb a
   * normal day's issue volume on a small/medium prod service,
   * bounded enough that a regression that spikes 5k new issue ids
   * in an hour can't flood the digest. Operator-tunable 0–500.
   */
  maxFindingsPerDay: z.number().int().min(0).max(500).default(50),
  /**
   * Sentry API base URL. Override only for self-hosted Sentry or
   * for routing through an enterprise outbound proxy. Empty =
   * hits api.sentry.io (the SaaS endpoint).
   */
  baseUrl: z.string().default(''),
});
export type ProdFeedbackSentryConfig = z.infer<typeof ProdFeedbackSentryConfigSchema>;

export const ProdFeedbackConfigSchema = z.object({
  sentry: ProdFeedbackSentryConfigSchema.default({
    enabled: false,
    orgSlug: '',
    projectSlug: '',
    authToken: '',
    targetProjectId: '',
    maxFindingsPerDay: 50,
    baseUrl: '',
  }),
});

/**
 * `pending-release-readiness` v0 — operator-tunable knobs for the
 * `/releases/<sha>` page. v0 ships 5 of the 8 designed tiles
 * (Tests / TIA / Flake / Visual diff / Prod error rate). Tiles whose
 * source signal is not configured render as `n/a` and are excluded
 * from the verdict math (avoids the "amber-because-undefined" trap
 * on a fresh install).
 */
export const ReleaseReadinessConfigSchema = z.object({
  /** Master kill switch. When `false` the route returns 404. */
  enabled: z.boolean().default(true),
  /**
   * Verdict cache TTL. The route serves a cached row when one
   * exists and is younger than this. POST .../refresh always
   * busts. Bounded so an op-trigger-happy flag never sits past
   * a week without a recompute.
   */
  cacheTtlHours: z.number().int().min(1).max(168).default(24),
  /**
   * Public read share-link gate. When `false` the share-link
   * endpoint always returns 403 — useful for orgs whose
   * compliance posture forbids public stakeholder URLs.
   */
  shareLinkEnabled: z.boolean().default(true),
  /**
   * Hard ceiling on how many shaped tile inputs the LLM summary
   * call can include. Caps cost on a worst-case 100k-failed-test
   * run. Tile payloads are truncated, not the verdict.
   */
  maxLlmInputTiles: z.number().int().min(1).max(20).default(10),
});
export type ReleaseReadinessConfig = z.infer<typeof ReleaseReadinessConfigSchema>;

/**
 * `pending-flake-detective` v0 — operator-tunable knobs for the
 * single-hypothesis-per-run flake diagnosis. Off by default; the
 * trigger gate (`minFlakeRateToDiagnose`) keeps cost bounded once
 * an operator flips `enabled: true`.
 */
/**
 * `pending-flake-detective` v0.5 — trace-artifact-backed recovery
 * path. Bounded zip-reader limits are operator-tunable so a
 * deployment with tighter resource budgets can dial them down
 * without a code change. Defaults below were picked to:
 *
 *   - Cover a realistic Playwright trace.zip (~5-15 MB inflated).
 *   - Stay well below the worker heap budget (50 MB total cap).
 *   - Abort within 3 s if a malicious zip exploits deflate
 *     amplification or a slow-to-parse line.
 *
 * The recovery path is opt-in via `enabled: false` by default;
 * operators turn it on once they've confirmed their runner is
 * producing valid Playwright traces.
 */
export const FlakeDetectiveTraceArtifactFallbackSchema = z.object({
  /** Master kill switch for the v0.5 trace-zip recovery path. */
  enabled: z.boolean().default(false),
  /** Per-entry uncompressed byte cap. 1 KiB .. 50 MB. */
  maxEntryBytes: z.number().int().min(1024).max(50_000_000).default(5_000_000),
  /** Total uncompressed byte cap across all entries. 1 KiB .. 200 MB. */
  maxTotalBytes: z.number().int().min(1024).max(200_000_000).default(50_000_000),
  /** Wall-clock parse cap in milliseconds. 100 ms .. 60 s. */
  parseTimeoutMs: z.number().int().min(100).max(60_000).default(3_000),
  /** Cap on lifted network requests. 0 .. 1000. */
  maxNetworkRequests: z.number().int().min(0).max(1000).default(200),
  /** Cap on lifted console messages. 0 .. 1000. */
  maxConsoleMessages: z.number().int().min(0).max(1000).default(200),
  /** Cap on lifted DOM mutations. 0 .. 1000. */
  maxDomMutations: z.number().int().min(0).max(1000).default(200),
});
export type FlakeDetectiveTraceArtifactFallback = z.infer<typeof FlakeDetectiveTraceArtifactFallbackSchema>;

export const FlakeDetectiveConfigSchema = z.object({
  /** Master kill switch. v0 ships OFF — operator opts in. */
  enabled: z.boolean().default(false),
  /**
   * Minimum rolling flake_rate to diagnose. Anything below the
   * threshold is treated as a one-off failure, not a flake. v0
   * default 0.10 = 10% over the dedup window.
   */
  minFlakeRateToDiagnose: z.number().min(0).max(1).default(0.1),
  /**
   * Days of dedup. The same (flow, hypothesis_category) pair
   * within this window writes one hypothesis row, not N. Cap is
   * 30 days — anything longer should be a per-org compliance
   * call, not a v0 default.
   */
  dedupWindowDays: z.number().int().min(1).max(30).default(7),
  /**
   * v0.5 — recovery path. When the v0.4 error-text classifier
   * lands on `'other'` AND the run has a ready trace.zip in the
   * registry, we open the trace.zip and lift the network /
   * console / DOM events to re-run the classifier. Strict
   * per-entry + total-byte + parse-timeout caps keep the reader
   * memory-safe against a malicious trace.
   */
  traceArtifactFallback: FlakeDetectiveTraceArtifactFallbackSchema.default({
    enabled: false,
    maxEntryBytes: 5_000_000,
    maxTotalBytes: 50_000_000,
    parseTimeoutMs: 3_000,
    maxNetworkRequests: 200,
    maxConsoleMessages: 200,
    maxDomMutations: 200,
  }),
});
export type FlakeDetectiveConfig = z.infer<typeof FlakeDetectiveConfigSchema>;
export type ProdFeedbackConfig = z.infer<typeof ProdFeedbackConfigSchema>;

/**
 * `pending-localization-smoke` v0.1 — operator-tunable knobs for
 * the locale fan-out matrix.
 *
 * Mirrors the `visualAi.matrix` shape so admin-settings UX can
 * reuse the same kill-switch + cap + defaults pattern. Off by
 * default (operator opts in).
 *
 * `defaultLocales` is the seed list the SPA grid CTA pre-fills
 * when an operator clicks "Run matrix" without explicit locale
 * selection — mirrors the 12-locale recommended default from
 * locked decision §1. Each entry must come from the canonical
 * 13-locale registry shipped in `pending-localization-smoke` v0;
 * the service validates this at submit-time.
 *
 * `defaultPseudoMode` is the seed value for the matrix-wide
 * mode. `'both'` is locked-decision §2.
 *
 * `maxLocales` enforces the locked-decision §4 cap (24). The
 * service raises `LOCALIZATION_MATRIX_TOO_MANY_LOCALES` at
 * submit-time when exceeded.
 */
/**
 * `pending-design-aware-visual-checks` v0.1 — operator knobs for the
 * Figma PAT resolver + per-pin SSIM threshold default + cache TTL.
 *
 * The v0 ship landed Migration 122 `design_pins` + the SSIM scorer +
 * the Figma URL parser; v0.1 lights the kill switch + PAT + cache TTL
 * so the new resolver service can actually fetch PNG URLs from
 * Figma.
 *
 * Locked decisions (design doc):
 *   - §4: PAT only in v0.1 (OAuth is v2). Encrypted at rest via
 *     crypto-vault (`encrypt()` on PATCH, `decrypt()` on read).
 *   - §5: 0.95 is the SSIM default; operator can tune up to 0.999
 *     for super-strict pixel-perfect runs.
 *   - §6: 24h (86400s) cache default. The resolver re-resolves any
 *     row older than this TTL even if `design_frame_cache` has a
 *     hit.
 */
export const DesignAwareConfigSchema = z.object({
  /** Master kill switch. v0.1 ships OFF — operator opts in. */
  enabled: z.boolean().default(false),
  /**
   * Figma Personal Access Token. Stored encrypted at rest via
   * `crypto-vault` (3-part dotted `<iv>.<tag>.<ct>` shape).
   * `getPublicConfig` exposes only `hasFigmaToken: !!figmaToken`.
   */
  figmaToken: z.string().default(''),
  /**
   * Sketch Cloud Personal Access Token (v0.2). Stored encrypted at
   * rest. Public config exposes only `hasSketchToken`.
   * Sketch's Cloud REST root is hard-wired to `https://api.sketch.cloud`
   * (no self-hosted variant exists), so there is no base-URL knob.
   */
  sketchToken: z.string().default(''),
  /**
   * Penpot Personal Access Token (v0.2). Stored encrypted at rest.
   * Public config exposes only `hasPenpotToken`.
   */
  penpotToken: z.string().default(''),
  /**
   * Penpot REST base URL (v0.2). Defaults to the SaaS endpoint;
   * operators on a self-hosted Penpot instance override this to
   * point at their deployment. The dispatcher rejects any host that
   * doesn't match (CSRF / SSRF hardening — the runner won't follow
   * an arbitrary URL just because a pin says so).
   */
  penpotBaseUrl: z.string().url().default('https://design.penpot.app'),
  /**
   * Default per-pin SSIM threshold. Locked decision §5: 0.95 is the
   * safe default (catches design-system drift without false-positives
   * during animation). Per-pin overrides win.
   */
  similarityThreshold: z.number().gt(0).lte(1).default(0.95),
  /**
   * Cache TTL in seconds. Locked decision §6: 24h. Operator-tunable;
   * minimum 60s (avoids resolver thrash on every run) and maximum 7
   * days (Figma signed-URL expiry forces a refresh anyway).
   */
  cacheSeconds: z
    .number()
    .int()
    .min(60)
    .max(7 * 86400)
    .default(86400),
});
export type DesignAwareConfig = z.infer<typeof DesignAwareConfigSchema>;

/**
 * `pending-privacy-compliance-scanner` v0.1 — admin-tunable privacy /
 * compliance scanner block. Locked decisions (per the design doc):
 *
 *   §1 post-run hook (no in-run interception) — the runtime layer
 *      reads this on every run-save.
 *   §3 profile is one of GDPR / HIPAA / SOC2 / custom.
 *   §4 default profile = GDPR (when enabled).
 *   §9 NO raw PII anywhere in admin-config — custom matchers store
 *      the PATTERN, never sample matches.
 *
 * Kill switch defaults to OFF so operators have to opt in (privacy
 * features that fire by default surprise auditors).
 */
export const PrivacyConfigSchema = z.object({
  /** Master kill switch. v0.1 ships OFF — operator opts in. */
  enabled: z.boolean().default(false),
  /** Active scan profile (rule catalogue). Locked decision §4 — default `gdpr`. */
  scanProfile: z.enum(['gdpr', 'hipaa', 'soc2', 'custom']).default('gdpr'),
  /**
   * Runner capture mode for outbound request bodies. `never` = capture
   * URL/method only (still catches `pii_in_url` / `third_party_call` /
   * `cookie_set_by_third_party`); `snippet` = capture the first 2KB
   * of every outbound request body (enables `pii_in_request_body`).
   *
   * Default `never` — capturing bodies has a measurable per-step
   * overhead, and most procurement asks are satisfied by URL +
   * third-party + cookie surface anyway.
   */
  captureBodies: z.enum(['never', 'snippet']).default('never'),
  /**
   * Operator-defined custom matchers (closes the v1 row alongside
   * v0.1 — small enough to ship in the same chat). Each matcher is
   * a regex + severity; the runtime compiles + applies them
   * alongside the profile's built-in rule catalogue. Max 100 per
   * org (defensive bound — the scanner is O(rules * text-length)
   * per source).
   */
  customMatchers: z
    .array(
      z.object({
        id: z
          .string()
          .min(1)
          .max(40)
          .regex(/^[a-z0-9-]+$/),
        name: z.string().min(1).max(80),
        pattern: z.string().min(1).max(500),
        severity: z.enum(['high', 'med', 'low']),
      }),
    )
    .max(100)
    .default([]),
});
export type PrivacyConfig = z.infer<typeof PrivacyConfigSchema>;

export const LocalizationConfigSchema = z.object({
  /** Master kill switch. v0.1 ships OFF \u2014 operator opts in. */
  enabled: z.boolean().default(false),
  /**
   * Seed locale list the SPA grid CTA pre-fills. Empty array
   * means "no default \u2014 operator must pick explicitly".
   * Default is the 12-locale recommended set from locked
   * decision \u00a71.
   */
  defaultLocales: z
    .array(z.string().min(2).max(16))
    .max(24)
    .default([
      'en-US',
      'en-GB',
      'es-ES',
      'es-MX',
      'fr-FR',
      'de-DE',
      'it-IT',
      'pt-BR',
      'ja-JP',
      'ko-KR',
      'zh-CN',
      'ar-EG',
    ]),
  /** Seed pseudo-translation mode the CTA pre-fills. */
  defaultPseudoMode: z.enum(['bracketed', 'extended', 'both']).default('both'),
  /**
   * Per-org cap on locale count per matrix run. The locked-
   * decision \u00a74 cap is 24; operators may set lower to limit
   * cost. The service refuses values > 24 at config-save time.
   */
  maxLocales: z.number().int().min(2).max(24).default(24),
});
export type LocalizationConfig = z.infer<typeof LocalizationConfigSchema>;

/**
 * `pending-test-ownership-oncall` v0.1 (Ship 54) — operator-tunable
 * knobs for the auto-paging escalation worker that completes the
 * accountability loop above the data-layer shipped in v0 (Ship 43).
 *
 * Locked decisions (2026-05-31, design doc §2):
 *
 *   1. `enabled` defaults to `false` so the worker stays opt-in until
 *      orgs have populated `owner_user_id` for their critical flows.
 *      Once an org has 1+ owned flow + the admin has acknowledged the
 *      page-an-owner contract, they flip the knob.
 *   2. `consecutiveFailureDays` (range 1–7) — N consecutive calendar
 *      days of `failed` scheduled runs needed to trigger a page. The
 *      design doc pins the default at 2 so a single bad day doesn't
 *      page the owner (one-off flakes are absorbed by the flake-
 *      digest); a Friday + Monday pair, however, will fire because
 *      they're consecutive scheduled days.
 *   3. Workers do not page on ad-hoc runs (developer-initiated). The
 *      escalation contract is "your flow failed on the schedule it
 *      owns", which only makes sense for scheduled invocations.
 *   4. Flows with `owner_user_id IS NULL` are skipped — the dashboard
 *      unowned-count banner is the surface that pushes operators to
 *      claim ownership; this worker doesn't double-page until an
 *      owner exists.
 */
export const OwnershipEscalationConfigSchema = z.object({
  enabled: z.boolean().default(false),
  consecutiveFailureDays: z.number().int().min(1).max(7).default(2),
});
export type OwnershipEscalationConfig = z.infer<typeof OwnershipEscalationConfigSchema>;

export const OwnershipConfigSchema = z.object({
  escalation: OwnershipEscalationConfigSchema.default({
    enabled: false,
    consecutiveFailureDays: 2,
  }),
});
export type OwnershipConfig = z.infer<typeof OwnershipConfigSchema>;

/**
 * `pending-revenue-weighted-coverage` v0.1 — operator-tunable knobs
 * for the dollar-weighted coverage view (release-readiness tile + PR
 * comment line). v0 shipped manual tagging + a pure scoring helper;
 * v0.1 wires the helper into the release-readiness verdict and the
 * PR comment, and lets operators tune the per-tier weights so they
 * can match their own internal revenue-importance ordering.
 *
 * Weights are bounded to `[0, 1]` per locked decision §2 (default
 * `high: 1.0, med: 0.5, low: 0.1`).
 */
export const CoverageConfigSchema = z.object({
  /**
   * Per-tier weights for the revenue-weighted coverage math. The
   * scoring helper multiplies each covered/total flow count by the
   * matching weight before summing; `weightedPercent =
   * coveredWeight / totalWeight` (excluding NULL-tier flows).
   */
  revenueTierWeights: z
    .object({
      high: z.number().min(0).max(1).default(1.0),
      med: z.number().min(0).max(1).default(0.5),
      low: z.number().min(0).max(1).default(0.1),
    })
    .default({ high: 1.0, med: 0.5, low: 0.1 }),
  /**
   * `pending-revenue-weighted-coverage` v1 (2026-05-31) — currency
   * layer. Per-tier dollar value lets the release-readiness tile +
   * PR comment render `$X of $Y revenue covered` instead of just
   * the percent. Default `0` per tier means the operator has NOT
   * opted in; the tile + PR composer keep the v0.1 percent-only
   * shape until at least one tier is non-zero.
   *
   * Bounded `[0, 1_000_000_000]` per tier so an obviously wrong
   * configuration value (e.g. `1e18`) is rejected at the schema
   * layer instead of corrupting the UI.
   */
  dollarValuePerTier: z
    .object({
      high: z.number().min(0).max(1_000_000_000).default(0),
      med: z.number().min(0).max(1_000_000_000).default(0),
      low: z.number().min(0).max(1_000_000_000).default(0),
    })
    .default({ high: 0, med: 0, low: 0 }),
  /**
   * v1 — ISO 4217 3-letter currency code. Validated as 3 uppercase
   * letters; the renderer feeds this to
   * `Intl.NumberFormat(..., { style: 'currency', currency })` which
   * gracefully handles every code the runtime knows. The API never
   * converts between currencies — the operator picks one and every
   * tier value is denominated in that currency.
   */
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/, 'Currency must be a 3-letter ISO 4217 code (e.g. "USD", "EUR", "INR").')
    .default('USD'),
});
export type CoverageConfig = z.infer<typeof CoverageConfigSchema>;

/**
 * Canonical schema for the admin_config DB row. Single source of truth
 * for both the API (validates inbound writes) and admin-settings UI
 * (renders edit controls + types the response of GET /api/admin/config).
 *
 * Adding a new operator-tunable setting:
 *   1. Add the field to the right section schema below.
 *   2. Add the matching default in DEFAULT_ADMIN_CONFIG.
 *   3. Add the UI control in admin-settings/src/features/<section>/.
 *   4. Read it via getConfig() wherever needed (never process.env).
 *
 * See docs/CONFIG.md for the bootstrap-vs-DB decision rule.
 */

// ---- Connection ----
export const ConnectionSchema = z.object({
  apiBaseUrl: z.string(),
  wsUrl: z.string(),
  healthCheckTimeoutMs: z.number().int().positive(),
  wsReconnectMaxDelayMs: z.number().int().positive(),
});
export type Connection = z.infer<typeof ConnectionSchema>;

// ---- Server / API process tunables ----
export const ServerConfigSchema = z.object({
  apiPort: z.number().int().positive(),
  corsOrigin: z.string(),
  rateLimitMax: z.number().int().nonnegative(),
  rateLimitWindowMs: z.number().int().positive(),
  jwtExpiry: z.string(),
  logLevel: z.string(),
  /**
   * `pending-targeted-debug-observability` v0 — ambient log sampling.
   *
   * When > 0 and no explicit debug rule / route override fires, a
   * deterministic fraction of requests is promoted to debug level
   * (bucketed by `req.id` hash so a request is wholly sampled or
   * not). Use 0.01-0.05 for production "ambient signal" without
   * doubling log volume; 0 disables the path entirely (default).
   */
  logSampleRate: z.number().min(0).max(1).default(0),
  /**
   * `pending-targeted-debug-observability` v0 — per-route log-level
   * override map. Keys are regex sources matched against `req.path`;
   * values are pino levels (`debug` | `trace` | `info`). A route
   * matching a key is promoted to that level for every request,
   * skipping the global-level filter. Use this for "I want every
   * /api/intent request at debug for the next hour" investigations
   * that don't fit cleanly into a per-user or per-request rule.
   */
  routeLogLevels: z.record(z.string(), z.string()).default({}),
  /**
   * `pending-targeted-debug-observability` v0 — time-boxed global
   * log-level flip. ISO-8601 timestamp; when set and in the future,
   * the global level reverts to `info` after the timestamp passes.
   * Empty string / null means "no expiry" (the legacy permanent
   * flip). The auto-revert sweep runs as a side-effect of the
   * existing 60s admin-config refresh.
   */
  logLevelExpiresAt: z.string().nullable().default(null),
  /**
   * `pending-targeted-debug-observability` v0 — in-memory ring
   * buffer size for /api/logs. Bumped from the legacy 1000 to 5000
   * so a 500 from 6 hours ago is still visible during a slow-burn
   * incident. Hard caps at 100k to bound RSS.
   */
  logBufferSize: z.number().int().min(100).max(100_000).default(5000),
  runnerPort: z.number().int().positive(),
  screenshotDir: z.string(),
});
export type ServerConfig = z.infer<typeof ServerConfigSchema>;

// ---- Branding ----
export const BrandingSchema = z.object({
  supportEmail: z.string().email().or(z.literal('')),
  /**
   * Absolute URL of the public marketing site. Empty = marketing lives
   * on the same host as the product (QA / UAT / local). Production sets
   * `https://rynora.ai` so flow-builder "Back to home" leaves `/app`.
   */
  marketingUrl: z.string().default(''),
});
export type Branding = z.infer<typeof BrandingSchema>;

// ---- Email ----
export const EmailSchema = z.object({
  /** SMTP is the existing path; 'resend' switches to the HTTPS API. */
  provider: z.enum(['smtp', 'resend']).default('smtp'),
  smtpHost: z.string(),
  smtpPort: z.number().int().nonnegative(),
  smtpSecure: z.boolean(),
  smtpUser: z.string(),
  smtpPass: z.string(),
  smtpFrom: z.string(),
  contactEmail: z.string(),
  /** Resend HTTPS API. Encrypted at rest via crypto-vault. */
  resendApiKey: z.string().default(''),
  resendFromDomain: z.string().default(''),
});
export type Email = z.infer<typeof EmailSchema>;

// ---- Auth ----
export const AuthConfigSchema = z.object({
  appUrl: z.string(),
  jwtSecret: z.string(),
  jwtExpiresIn: z.string(),
  googleClientId: z.string(),
  googleClientSecret: z.string(),
  microsoftClientId: z.string(),
  microsoftClientSecret: z.string(),
  microsoftTenant: z.string(),
  githubClientId: z.string(),
  githubClientSecret: z.string(),
  resetTokenExpiresHours: z.number().int().positive(),
  googleEnabled: z.boolean(),
  githubEnabled: z.boolean(),
  microsoftEnabled: z.boolean(),
  /**
   * IP-address salt used for hashing client IPs in unauthenticated
   * routes (contact, support-chat, waitlist) before storing for abuse
   * detection. Was IP_HASH_SALT env var, now operator-tunable.
   */
  ipHashSalt: z.string().default(''),
  /**
   * Stricter rate limit applied to auth endpoints specifically. Was
   * RATE_LIMIT_AUTH_MAX env var.
   */
  rateLimitAuthMax: z.number().int().nonnegative().default(20),
  /**
   * When TRUE, `/api/auth/register` (the email+password sign-up
   * route) inserts the new user with `email_verified = false` and
   * issues NO session JWT. The user is instead sent a verification
   * link via `email_verification_tokens` + the existing
   * `account.verify_email` mail template. They must click the link
   * (which sets `email_verified = true`) before `/api/auth/login`
   * will return a session token.
   *
   * When FALSE, register auto-verifies the row (`email_verified =
   * true`) and issues a session JWT immediately — the legacy
   * dev-mode behaviour. Operators who want a "frictionless" demo
   * env can keep this off.
   *
   * OAuth sign-ins (Google, Microsoft, GitHub) are unaffected — the
   * identity provider already verified the address, so those flows
   * always set `email_verified = true` regardless of this switch.
   *
   * Default: TRUE — the secure default. Existing
   * post-hardcoded-auto-verify rows in production all have
   * `email_verified = true` already (because register always set
   * it), so flipping the default on does not lock anyone out; it
   * only affects future sign-ups.
   */
  requireEmailVerification: z.boolean().default(true),
});
export type AuthConfig = z.infer<typeof AuthConfigSchema>;

// ---- AI ----
//
// `intentProvider` is the platform-wide default. Each per-feature
// provider (`explainProvider`, `healingProvider`, `debugProvider`,
// `rcaProvider`) is optional; when left as the empty string the
// runtime resolver falls back to `intentProvider` so existing
// deployments keep their current routing without an admin edit.
//
// Anthropic creds were added in Groq Batch 2; they live alongside
// OpenAI / Groq so an operator can pin Claude for the heavy
// reasoning paths (RCA / agent-explore) while keeping Groq for the
// hot, latency-sensitive parse / explain paths.
export const AiConfigSchema = z.object({
  intentProvider: z.string(),
  /**
   * Per-feature routing overrides. Empty string = "use intentProvider".
   * Allowed values match `IntentEngineConfig.provider`:
   * `mock` | `local` | `openai` | `groq` | `anthropic`.
   */
  explainProvider: z.string().default(''),
  healingProvider: z.string().default(''),
  debugProvider: z.string().default(''),
  rcaProvider: z.string().default(''),
  /**
   * Phase 11 — per-feature override for the next-action chip strip's
   * AI label-polish endpoint. Empty = inherits intentProvider. The
   * recommended pin is `'groq'` so the polish call rides Groq's free
   * tier without affecting the default provider for other features.
   */
  suggestActionsProvider: z.string().default(''),
  /**
   * Migration 088 — `pending-contrast-advisor-a11y` Phase D.
   * Override the AI provider for the contrast-advisor's AI
   * rewrite endpoint (`POST /api/intent/a11y-fix`). Empty
   * string falls back to the default `intentProvider`.
   */
  a11yFixProvider: z.string().default(''),
  /**
   * `defer-llm-tool-use` — kill-switch + per-feature override for
   * the function/tool-calling endpoint (`POST /api/intent/tool-call`).
   * Disabled by default so an operator opts in once a provider that
   * supports tool use is configured. Empty `toolUseProvider` falls
   * back to `intentProvider`. Tool use is supported on `openai`,
   * `groq` (OpenAI-compatible), and `anthropic`.
   */
  toolUseEnabled: z.boolean().default(false),
  toolUseProvider: z.string().default(''),
  /**
   * `defer-debug-vision` — kill-switch + per-feature override for
   * the screenshot vision extractor endpoint
   * (`POST /api/intent/vision`). Disabled by default. The recommended
   * pin is OpenAI's `gpt-4o-mini` (cheap vision) or Anthropic's
   * sonnet (better extraction of structured UI text).
   */
  visionEnabled: z.boolean().default(false),
  visionProvider: z.string().default(''),
  /**
   * `pending-tag-search-saved-views-v2` — kill-switch + per-feature
   * override for the AI tag-suggest endpoint (`POST /api/tags/suggest`).
   * Disabled by default; when enabled but no provider is set, the
   * route falls back to the deterministic keyword heuristic (same
   * shape it would return from an LLM, just sourced locally). The
   * recommended pin is `'groq'` because tag suggestions are cheap,
   * latency-sensitive, and the deterministic fallback already covers
   * the happy path — no need to pay OpenAI / Anthropic prices.
   */
  tagSuggestEnabled: z.boolean().default(false),
  tagSuggestProvider: z.string().default(''),
  /**
   * Phase 6b (Stage C — `stage-c-paid`) — per-feature override for the
   * AI script-generation endpoint (`POST /api/scripts/generate`,
   * the "Generate with AI" button on the Custom Script step).
   * Empty string falls back to `intentProvider`. Code generation is
   * latency-tolerant but quality-sensitive, so the recommended pin is
   * `'openai'` with `gpt-4o-mini` for production traffic — Groq with
   * `llama-3.3-70b-versatile` is the cheap-but-good v1 option that's
   * already wired in QA, and Anthropic Claude is the long-context
   * fallback when the user supplies large input/output schemas.
   * Tier gate (`aiAuthor`) is enforced separately in `plans.ts`;
   * setting this provider does NOT enable the feature for tiers that
   * have `aiAuthor: false`.
   */
  scriptAuthorProvider: z.string().default(''),
  /**
   * `pending-rynora-agent` (migration 114, 2026-05-28) — per-feature
   * override for the autonomous QA teammate. Drives draft-flow
   * authoring (describe-mode + NL-step pipeline) inside the agent's
   * nightly loop. Empty string falls back to `intentProvider`. The
   * recommended pin is `'anthropic'` for the higher-quality NL→step
   * parsing inside the agent's "draft a flow for this URL" call,
   * because the cost surface is bounded (capped by
   * `agent.maxDraftsPerDay`) and the quality delta on the draft step
   * payload directly impacts approval rate. Operators on a strict
   * cost budget should pin `'groq'` instead — the draft is
   * intentionally minimal in v0 (navigate + URL-assert) so even the
   * cheap model produces a usable stub.
   */
  agentProvider: z.string().default(''),
  /**
   * `pending-prod-feedback-loop` (migration 115, 2026-05-28) —
   * per-feature override for the Sentry → reproduction-flow drafting
   * call. Declared in v0 so v0.1 (the LLM authoring step) does not
   * need a contracts change to flip on; v0 itself does NOT call the
   * provider (the worker only ingests issues into agent_findings).
   * Empty string falls back to `intentProvider`. Recommended pin is
   * `'anthropic'` for the higher-quality "translate a stack trace +
   * breadcrumbs into a navigate + click + assert flow" reasoning;
   * cost is bounded by `prodFeedback.sentry.maxFindingsPerDay`.
   */
  prodErrorProvider: z.string().default(''),
  /**
   * `pending-release-readiness` v0 — per-feature override for the
   * 2-sentence LLM verdict summary on the `/releases/<sha>` page.
   * Empty string falls back to `intentProvider`. Recommended pin
   * is `'anthropic'` (the verdict copy is the user-visible artefact
   * and quality matters more than latency); cost is bounded by
   * `releaseReadiness.cacheTtlHours` (default 24h) so the LLM is
   * called once per commit-SHA, not once per page-load.
   */
  releaseVerdictProvider: z.string().default(''),
  /**
   * `pending-quality-hq` v0.1 — Story → risks. Serves the risk
   * proposal on `POST /api/projects/:id/test-authoring/generate`,
   * where the business-risk list is the leading artefact and cases
   * are the follow-up. Empty string inherits `intentProvider`. Cost
   * is one call per story generate (the same call that returns
   * cases), so the recommended pin is whatever already serves story
   * generation. An org-level AI opt-out skips the call entirely.
   */
  qualityRiskProvider: z.string().default(''),
  /**
   * `pending-flake-detective` v0 (migration 119) — per-feature
   * override for the single-hypothesis-per-run flake diagnosis
   * call. Empty string falls back to `intentProvider`. Bounded
   * by `flakeDetective.minFlakeRateToDiagnose` + the 7-day
   * dedup window so cost is implicit-capped.
   */
  flakeHypothesisProvider: z.string().default(''),
  /**
   * Locator Strategy Overhaul · Phase 4.2 (2026-06-08) — locator
   * disambiguation tie-breaker. Routes the LLM call the runner
   * makes when fingerprint-scored resolution returns >1 candidate
   * above the 0.65 confidence threshold. Decisions are cached per
   * (fingerprint hash, candidate hashes, intent text) so the
   * second run skips the LLM call entirely. Empty string ('') →
   * fall back to `intentProvider`. Recommended pin: `'groq'`
   * (latency + cost-sensitive; the picks are simple "A or B"
   * decisions where small-model accuracy already beats human
   * baseline).
   */
  locatorTiebreakerProvider: z.string().default(''),
  /**
   * Scenario Repository — Gherkin step generation from a prompt
   * (`POST .../scenarios/generate-steps`). Empty string inherits
   * `intentProvider`. Recommended pin: `'groq'` (cheap, structured
   * JSON output).
   */
  scenarioStepsProvider: z.string().default(''),
  /**
   * Scenario Repository — bulk draft scenario generation
   * (`POST .../scenarios/generate-drafts`). Empty string inherits
   * `intentProvider`.
   */
  scenarioDraftsProvider: z.string().default(''),
  /**
   * Failure Intelligence — execution failure insight narrative
   * (`POST .../failures/:id/analyze`). Empty string inherits
   * `intentProvider`. Recommended pin: `'anthropic'` for RCA quality.
   */
  failureAnalysisProvider: z.string().default(''),
  /**
   * Analyze Application — AI Understanding step (bounded evidence pack →
   * advisory domains/modules/flows). Default pin `'groq'` (free/low-cost).
   * Does not inherit `intentProvider` when empty would fall through to mock —
   * empty string still falls back to intentProvider for BYOK flexibility, but
   * DEFAULT_CONFIG sets `'groq'` so Analyze works when Intent stays on mock.
   */
  knowledgeAnalyzeProvider: z.string().default('groq'),
  /**
   * Requirement Intelligence — requirement quality proposal
   * (`POST .../requirements/analyze`). Empty inherits `intentProvider`.
   * Metering class: Authoring (debits `aiCallsPerMonth`).
   */
  requirementIntelligenceProvider: z.string().default(''),
  /**
   * Change Impact agent — explanation over deterministic findings
   * (`POST .../scenarios/change-impact/explain`). Empty inherits
   * `intentProvider`. Metering class: Background (never debits plan counter).
   */
  changeImpactProvider: z.string().default(''),
  openaiApiKey: z.string(),
  openaiModel: z.string(),
  openaiBaseUrl: z.string(),
  /**
   * Groq is OpenAI-API-compatible; we keep its credentials separate so an
   * operator can A/B providers per env without rotating openaiApiKey.
   */
  groqApiKey: z.string().default(''),
  groqModel: z.string().default('mixtral-8x7b-32768'),
  /**
   * Anthropic Claude. `anthropicBaseUrl` is for self-hosted Claude-
   * compatible gateways; leave empty to hit api.anthropic.com.
   */
  anthropicApiKey: z.string().default(''),
  anthropicModel: z.string().default('claude-3-5-sonnet-20241022'),
  anthropicBaseUrl: z.string().default(''),
  /**
   * `pending-sovereign-ai-tier` v0 — local Ollama daemon. The
   * canonical sovereign / air-gapped adapter. `ollamaApiKey` is
   * intentionally optional: Ollama itself does not require auth
   * (network policy is the gate). Operators front-ending Ollama
   * with a reverse proxy that enforces a shared secret should
   * paste it here. Empty `ollamaBaseUrl` defaults to
   * `http://localhost:11434/v1` — fine for dev / smoke tests, but
   * production deployments will almost always pin a private
   * hostname (`http://ollama.internal:11434/v1`).
   */
  ollamaApiKey: z.string().default(''),
  ollamaModel: z.string().default('llama3.1:8b'),
  ollamaBaseUrl: z.string().default(''),
  /**
   * `defer-sovereign-ai-vllm-adapter` v0 — sibling of the Ollama
   * adapter for sovereign / on-prem deployments that need batched-
   * throughput (>100 RPS concurrent inference) instead of Ollama's
   * single-stream serving. Operators launch vLLM with
   * `vllm serve <model>` and (optionally) `--api-key <secret>`;
   * paste the same secret here when set. Empty `vllmBaseUrl`
   * defaults to `http://localhost:8000/v1` — fine for dev / smoke
   * tests, but production deployments will almost always pin a
   * private hostname (`http://vllm.internal:8000/v1`).
   */
  vllmApiKey: z.string().default(''),
  vllmModel: z.string().default('meta-llama/Meta-Llama-3.1-8B-Instruct'),
  vllmBaseUrl: z.string().default(''),
  /**
   * `defer-llm-org-byok` — global kill switch for the org-level
   * `org_llm_credentials` lookup. Default `true` so deployments
   * that have run migration 103 get the feature; flipping to
   * `false` makes `getEngine` skip the org-BYOK arm entirely
   * (useful while debugging a misconfigured key without forcing
   * every org admin to delete their row).
   */
  orgByokEnabled: z.boolean().default(true),
  /**
   * `defer-llm-streaming` — global kill switch for
   * `POST /api/intent/parse-stream`. Default `true` for wire
   * compatibility (Phase 8.20 soak — no production UI; operators
   * may flip to `false` after observing zero traffic to force
   * clients onto non-stream `/parse`).
   */
  streamingEnabled: z.boolean().default(true),
  /**
   * Phase 8.20 — kill switch for soft-deprecated Phase-5 Intent
   * orphans (`/suggest`, `/rca`, `/semantic-assert`, `/adversarial`,
   * `/explore`, `/detect-dynamic`). Default `true` (SOAK — leave
   * reachable until external traffic is confirmed absent). Flip to
   * `false` → HTTP 410. Does not affect canonical `/parse`,
   * `/explain`, `/vision`, or `/tool-call` (own flags).
   */
  intentPhase5OrphansEnabled: z.boolean().default(true),
});
export type AiConfig = z.infer<typeof AiConfigSchema>;

// ---- Billing ----
export const BillingSchema = z.object({
  stripePublishableKey: z.string(),
  stripeSecretKey: z.string(),
  stripeWebhookSecret: z.string(),
  stripePrices: z.object({
    starter: z.string(),
    pro: z.string(),
    team: z.string(),
    enterprise: z.string(),
  }),
  encryptionKey: z.string(),
});
export type Billing = z.infer<typeof BillingSchema>;

// ---- Payments (multi-gateway, migration 074) ----
//
// Operator-selectable payment provider with one credential block per
// supported gateway. Schema mirrors `cloudBrowser` exactly: the enum
// is forward-compatible (lists every provider we plan to wire), and
// `IMPLEMENTED_PAYMENT_PROVIDERS` is the live allowlist consulted by
// the route layer to short-circuit unwired providers with
// `PAYMENT_PROVIDER_NOT_IMPLEMENTED`.
//
// Per-org overrides live on `organizations.payment_provider`. The
// resolution order is: explicit org override -> this admin default ->
// hard fallback `'stripe'`.
//
// Adding a new provider:
//   1. Add the enum entry below.
//   2. Add a credential block (keys + webhook secret + region/env
//      toggles as relevant).
//   3. Drop a `<provider>.ts` adapter into
//      `packages/api/src/lib/payment-providers/`.
//   4. Add the provider id to `IMPLEMENTED_PAYMENT_PROVIDERS`.
//   5. Add the admin UI section in
//      `packages/admin-settings/src/App.tsx` under the Payments tab.
export const PaymentProviderSchema = z.enum(['stripe', 'razorpay', 'paddle', 'paypal', 'lemonsqueezy', 'manual']);
export type PaymentProvider = z.infer<typeof PaymentProviderSchema>;

/**
 * Allowlist of providers with a wired adapter. Anything not in here
 * fails fast at the route layer with `PAYMENT_PROVIDER_NOT_IMPLEMENTED`.
 *
 * IMPORTANT: keep this in sync with
 * `packages/api/src/lib/payment-providers/registry.ts` (which factories
 * by id) and the admin UI's "Coming soon" badge.
 */
export const IMPLEMENTED_PAYMENT_PROVIDERS: readonly PaymentProvider[] = [
  'stripe',
  'razorpay',
  'paddle',
  'paypal',
  'lemonsqueezy',
  'manual',
] as const;

export const PaymentsSchema = z.object({
  /** Active provider when an org has no explicit override on
   *  `organizations.payment_provider`. Default `'stripe'` so existing
   *  installs keep their behaviour after migration 074 lands. */
  provider: PaymentProviderSchema.default('stripe'),

  /** Master kill-switch for ALL payment providers. When false the
   *  /api/billing/checkout + /portal endpoints 503 with
   *  `PAYMENT_PROVIDER_NOT_CONFIGURED` regardless of provider. */
  enabled: z.boolean().default(true),

  // ---- Razorpay (India: UPI, NetBanking, Wallets, Cards, GST) ----
  /** Razorpay Key ID (rzp_test_... or rzp_live_...). Public, embedded in checkout JS. */
  razorpayKeyId: z.string().default(''),
  /** Razorpay Key Secret. Encrypted via crypto-vault on write; redacted on read. */
  razorpayKeySecret: z.string().default(''),
  /** Razorpay webhook secret (X-Razorpay-Signature HMAC-SHA256 key). Encrypted at rest. */
  razorpayWebhookSecret: z.string().default(''),
  /** Plan IDs created in Razorpay Dashboard, mapped to Rynora plan tiers. */
  razorpayPlans: z
    .object({
      starter: z.string().default(''),
      pro: z.string().default(''),
      team: z.string().default(''),
      enterprise: z.string().default(''),
    })
    .default({ starter: '', pro: '', team: '', enterprise: '' }),

  // ---- Paddle (Merchant of Record; auto VAT/GST/sales tax) ----
  /** Paddle Vendor ID (numeric, public). */
  paddleVendorId: z.string().default(''),
  /** Paddle API key (Bearer token for Paddle Billing v4). Encrypted at rest. */
  paddleApiKey: z.string().default(''),
  /** Paddle webhook secret (Paddle-Signature header HMAC). Encrypted at rest. */
  paddleWebhookSecret: z.string().default(''),
  /** sandbox = api.sandbox.paddle.com, live = api.paddle.com. */
  paddleEnvironment: z.enum(['sandbox', 'live']).default('sandbox'),
  /** Paddle Price IDs mapped to Rynora plan tiers. */
  paddlePrices: z
    .object({
      starter: z.string().default(''),
      pro: z.string().default(''),
      team: z.string().default(''),
      enterprise: z.string().default(''),
    })
    .default({ starter: '', pro: '', team: '', enterprise: '' }),

  // ---- PayPal (Subscriptions REST) ----
  /** PayPal REST app Client ID. Public. */
  paypalClientId: z.string().default(''),
  /** PayPal REST app secret. Encrypted at rest. */
  paypalSecret: z.string().default(''),
  /** PayPal webhook id (used by /v1/notifications/verify-webhook-signature). */
  paypalWebhookId: z.string().default(''),
  /** sandbox = api-m.sandbox.paypal.com, live = api-m.paypal.com. */
  paypalEnvironment: z.enum(['sandbox', 'live']).default('sandbox'),
  /** PayPal billing-plan ids mapped to Rynora plan tiers. */
  paypalPlans: z
    .object({
      starter: z.string().default(''),
      pro: z.string().default(''),
      team: z.string().default(''),
      enterprise: z.string().default(''),
    })
    .default({ starter: '', pro: '', team: '', enterprise: '' }),

  // ---- Lemon Squeezy (alt MoR; cheaper than Paddle for indie volume) ----
  /** Lemon Squeezy Store ID (numeric). */
  lemonsqueezyStoreId: z.string().default(''),
  /** Lemon Squeezy API key. Encrypted at rest. */
  lemonsqueezyApiKey: z.string().default(''),
  /** Lemon Squeezy webhook secret. Encrypted at rest. */
  lemonsqueezyWebhookSecret: z.string().default(''),
  /** Lemon Squeezy variant ids (one per plan). */
  lemonsqueezyVariants: z
    .object({
      starter: z.string().default(''),
      pro: z.string().default(''),
      team: z.string().default(''),
      enterprise: z.string().default(''),
    })
    .default({ starter: '', pro: '', team: '', enterprise: '' }),

  // ---- Manual / wire transfer / annual invoice ----
  /** Email address that receives a notification when a customer requests
   *  a manual invoice. Empty = no email; the row is still created. */
  manualOpsEmail: z.string().default(''),
  /** Free-form copy shown to the user on the manual-invoice page
   *  (bank details, NEFT instructions, PO process, etc.). */
  manualInstructions: z.string().default(''),
});
export type Payments = z.infer<typeof PaymentsSchema>;

// ---- Observability ----
export const ObservabilitySchema = z.object({
  sentryDsnApi: z.string(),
  sentryDsnFrontend: z.string(),
  sentryEnvironment: z.string(),
  sentrySampleRate: z.number().min(0).max(1),
  sentryTracesSampleRate: z.number().min(0).max(1),
  betterstackLogsToken: z.string(),
  discordWebhookUrl: z.string(),
  discordCriticalWebhookUrl: z.string(),
  /**
   * Weekly digest schedule (DOW = 0..6, hour = 0..23 UTC). Was
   * RYNORA_WEEKLY_DIGEST_DOW / _HOUR env vars.
   */
  weeklyDigestDow: z.number().int().min(0).max(6).default(1),
  weeklyDigestHourUtc: z.number().int().min(0).max(23).default(9),
  /** Grafana Cloud — pull-based scrape on by default. */
  grafanaCloudUrl: z.string().default(''),
  grafanaServiceAccountToken: z.string().default(''),
  /**
   * Grafana Cloud Prometheus *instance* username (numeric, e.g. `3179284`).
   *
   * Distinct from the access-policy org id embedded in the
   * `glc_…` token's payload (e.g. `1759199`); the remote_write basic
   * auth needs the instance username, not the org id.
   *
   * Empty string means "use whatever username the consumer already
   * hardcoded" — set when introducing this field in 2026-05 to avoid
   * disturbing the QA Alloy install that already runs with the
   * username pasted into `/etc/alloy/config.alloy`. Once the deploy
   * pipeline templates the Alloy config from this field, the field
   * becomes mandatory for any operator that wants to push metrics.
   */
  grafanaPrometheusUsername: z.string().default(''),
  prometheusScrapeEnabled: z.boolean().default(true),
  /**
   * When `true` (default) the run-detail and `GET /api/runs/:id/heals`
   * endpoints include a one-sentence narrative ("Element label
   * changed; matched by partial label.") for each healed step.
   *
   * Setting this to `false` is the privacy / regulated-environment
   * escape hatch — the narrative is suppressed for `viewer`-role
   * users only; admins/editors still see it for debugging.
   *
   * Ties to design doc: docs/handbook/features/completed/ai-healing-explain.md
   */
  healNarrative: z.boolean().default(true),

  // -----------------------------------------------------------------
  // `pending-test-observability-tier` v2 finishers.
  // -----------------------------------------------------------------

  /**
   * Outbound webhook URL fired the FIRST time an
   * `error_fingerprints` row is created (i.e. occurrence_count
   * transitions from 0 → 1). Empty string disables the webhook.
   *
   * The body is a `FirstSeenWebhookPayload` (see
   * `@rynora/contracts/error-fingerprint`) JSON-encoded. If
   * `firstSeenWebhookSecret` is non-empty the request includes an
   * `X-Rynora-Signature` header carrying an HMAC-SHA256 over the
   * raw body — same shape as the monitor webhook adapter, so the
   * same downstream verifier can absorb both.
   *
   * Best-effort: a network failure logs + writes to audit-log
   * but never blocks fingerprint ingestion.
   */
  firstSeenWebhookUrl: z.string().default(''),
  firstSeenWebhookSecret: z.string().default(''),

  /**
   * Master kill switch for the AI-remediation endpoint. When off,
   * `POST /api/observability/fingerprints/:id/remediation`
   * returns 503 + `OBSERVABILITY_AI_REMEDIATION_DISABLED`
   * regardless of the configured `ai.rcaProvider`.
   *
   * Default OFF so operators opt in explicitly (the call hits an
   * LLM provider + sends fingerprint text out-of-network).
   */
  aiRemediationEnabled: z.boolean().default(false),

  /**
   * Per-org daily quota on AI-remediation calls (anti-runaway-cost).
   * Default `20`, max `500`. Counter lives in the existing
   * `llm_calls` table — we hit that view rather than introducing
   * a separate counter.
   */
  aiRemediationDailyQuotaPerOrg: z.number().int().min(0).max(500).default(20),
});
export type Observability = z.infer<typeof ObservabilitySchema>;

// ---- Runner ----
/**
 * Migration 086 — `pending-contrast-advisor-a11y`. Operator-tunable
 * defaults for the contrast-advisor check kind. The wcagLevel
 * here applies to every new flow's `accessibility-contrast` step
 * unless the step itself overrides via a per-step `wcag_level`
 * field. The pure WCAG threshold table is canonicalised in
 * `@rynora/contracts/contrast-advisor`; we only need the operator
 * level + on/off toggle here.
 *
 * v2 follow-ups (NOT here):
 *   • `brandPaletteId` — points at a curated palette so suggested
 *     fixes snap to brand colours.
 *   • `crossStateEnabled` — opts into hover / focus / active state
 *     checks (requires runner integration).
 */
export const AccessibilityConfigSchema = z.object({
  contrastWcagLevel: z.enum(['AA', 'AAA']).default('AA'),
  /** Master kill-switch — operator can disable the contrast
   *  check entirely without un-mounting the step kind. */
  contrastCheckEnabled: z.boolean().default(true),
});
export type AccessibilityConfig = z.infer<typeof AccessibilityConfigSchema>;

/**
 * `pending-postman-mock-server` Phase E — mock-server polish.
 * Three operator-tunable knobs:
 *
 *   • `publicShareLinksEnabled` — master kill-switch for the
 *     public dispatcher. Off → existing mocks keep working but
 *     a 3rd-party consumer hitting `/mocks/share/...` gets 403.
 *   • `proxyAllowlist` — allowlist of upstreams the `proxy`
 *     mode is permitted to forward to. Entries can be a host
 *     (`api.example.com`), a scheme+host
 *     (`https://api.example.com`), or a full URL prefix.
 *     Default `[]` means proxy mode is disabled until the
 *     operator opts in.
 *   • `defaultShareLinkRateLimitPerMin` — default per-token
 *     rate limit, overridable per mock under
 *     `api_mocks.share_link_rate_limit_per_min`.
 */
export const MockServerConfigSchema = z.object({
  publicShareLinksEnabled: z.boolean().default(true),
  proxyAllowlist: z.array(z.string()).default([]),
  defaultShareLinkRateLimitPerMin: z.number().int().min(1).max(60_000).default(100),
});
export type MockServerConfig = z.infer<typeof MockServerConfigSchema>;

/**
 * `pending-parallel-scripting-execution` v0 — how the runner pool
 * behaves when an `ensure-session` request arrives after
 * `maxConcurrentSessions` is already in use.
 *
 * - `reject` (v0 default): refuse with `RUNNER_AT_CAPACITY`. The FE
 *   shows a "wait or raise the limit" toast and lets the user retry.
 * - `queue` (reserved for v0.1): hold the request server-side until a
 *   slot frees up. Wire-level signalling is the same; the server
 *   replies with `{ type: 'queued' }` then `{ type: 'session-ready' }`
 *   once allocated. The schema accepts the value now so an admin can
 *   flip it without a contracts redeploy, but v0 keeps the runner-side
 *   behaviour identical to `reject` until the queue is implemented.
 */
export const RunnerAtCapacityPolicySchema = z.enum(['reject', 'queue']);
export type RunnerAtCapacityPolicy = z.infer<typeof RunnerAtCapacityPolicySchema>;

/**
 * Locator Strategy Overhaul · Phase 5.9 (2026-06-10) — operator-tunable
 * locator-strategy configuration. Closes the "Org-level Smart Locator
 * Priority UI" gap vs. KaneAI. v0 ships the highest-value knob —
 * extending the Test ID attribute list — and leaves room for v0.1
 * additions (strategy-order overrides, per-domain settings) without
 * a schema bump.
 *
 * Used by:
 *   - `packages/mvp/src/element-resolver.ts` — Tier 0 `data-testid`
 *     family emits one strategy per configured attribute.
 *   - `packages/mvp/src/ancestor-first-resolver.ts` —
 *     `buildAncestorSelector` queries every configured attribute when
 *     building the Test-ID arm of the OR-chain.
 *   - `packages/mvp/src/recording-script.ts` (future) — the in-page
 *     recorder picks the highest-precedence configured attribute when
 *     authoring a Test ID candidate.
 *
 * Defaults preserve today's hard-coded behaviour exactly:
 *   `['data-testid', 'data-test-id', 'data-cy']`.
 *
 * Operators adding `data-qa-id`, `data-test`, `data-automation-id`, etc.
 * just prepend their attribute name to this list — no code change.
 */
export const LocatorsConfigSchema = z.object({
  /**
   * Ordered list of HTML attribute names the resolver treats as Test
   * IDs. Strategies are emitted in this order, so the first attribute
   * here gets first crack at the locator chain. The list MUST contain
   * at least one entry — an empty list would disable Test-ID
   * resolution entirely, which is almost never what an operator
   * intends. Validation in the route layer rejects an empty input.
   */
  customTestIdAttributes: z
    .array(
      z
        .string()
        .min(1)
        .max(64)
        // Restrict to HTML attribute-name grammar so an operator can't
        // smuggle `]` / `[` / quote characters that would corrupt the
        // generated CSS selector. Matches the standard `data-*`
        // pattern plus a few legacy plain-name attributes.
        .regex(/^[a-zA-Z_:][a-zA-Z0-9_:.-]*$/, 'must be a valid HTML attribute name'),
    )
    .min(1, 'at least one Test ID attribute is required')
    .max(8, 'no more than 8 Test ID attributes can be configured at once')
    .default(['data-testid', 'data-test-id', 'data-cy']),
});
export type LocatorsConfig = z.infer<typeof LocatorsConfigSchema>;

export const RunnerSchema = z.object({
  browserTier: z.enum(['', 'steel', 'docker', 'cdp']),
  steelApiKey: z.string(),
  maxConcurrentSessions: z.number().int().positive(),
  /**
   * `pending-parallel-scripting-execution` v0 — policy applied when an
   * incoming run / interact request would exceed `maxConcurrentSessions`.
   * Default `reject` matches the v0 "wait or raise the limit" UX.
   */
  atCapacityPolicy: RunnerAtCapacityPolicySchema.default('reject'),
  defaultViewport: z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  }),
  /**
   * Filesystem path for Playwright trace artefacts. Was RYNORA_TRACE_DIR.
   */
  traceDir: z.string().default('/tmp/rynora-traces'),
  /**
   * Global kill switch for trace ingest + serve (Session 4a of the P1
   * head-to-head roadmap). Flow-level `globalConfig.traceMode` still
   * decides whether the runner asks for a trace per flow; this flag
   * is the operator-side gate that lets ops mass-disable trace storage
   * + downloads (compliance, disk pressure, cost). Default ON to
   * preserve back-compat with pre-Migration-081 deployments. When OFF
   * the API drops `trace_path` from incoming `POST /api/runs` writes
   * and `GET /api/traces/:id/download` returns 503 + TRACE_DISABLED.
   */
  traceEnabled: z.boolean().default(true),
  /**
   * Default trace retention window in days. The Session 4c retention
   * worker (parallel to the existing run-retention sweep) walks
   * `traces` where `expires_at < NOW()` and flips status='expired',
   * deleting the on-disk bytes. Operators can still raise the cap
   * per-plan via the existing run-retention policy.
   */
  traceRetentionDays: z.number().int().nonnegative().default(30),
  /**
   * Comma-separated allow-list of WebSocket origins for the mvp runner.
   * Was WS_ALLOWED_ORIGINS env var.
   */
  wsAllowedOrigins: z.string().default(''),
});
export type Runner = z.infer<typeof RunnerSchema>;

// ---- Integrations ----
/**
 * Migration 094 — `pending-deeper-jira`. Operator-tunable Jira
 * hook config. Lives at the top of AdminConfig (not under
 * integrations) so the registry can address it as `jira.*`
 * without overloading the legacy `integrations` block.
 */
export const JiraConfigSchema = z.object({
  enabled: z.boolean().default(false),
  componentMap: z.record(z.string(), z.string()).default({}),
  priorityMap: z
    .record(z.string(), z.string())
    .default({ critical: 'Highest', major: 'High', minor: 'Medium', trivial: 'Low' }),
  autoAddToActiveSprint: z.boolean().default(false),
  epicTagPattern: z.string().default('^epic-([A-Z]+-\\d+)$'),
  /**
   * `pending-deeper-jira-v2-git-blame-assignee` (v0) — add `git_blame`
   * to the existing assignee strategy enum. The deeper-Jira hook
   * walks the configured repo's most-recent commit, pulls the
   * author email, and looks it up against Jira's user search to
   * find an Atlassian accountId. When any step returns null we
   * fall through (git_blame → flow_owner_scim → project_lead →
   * none) so a misconfigured repo or unmapped author can't block
   * the issue creation entirely.
   */
  defaultAssigneeStrategy: z.enum(['none', 'project_lead', 'flow_owner_scim', 'git_blame']).default('none'),
  /**
   * `pending-deeper-jira-v2-git-blame-assignee` (v0) — repo to
   * blame against when `defaultAssigneeStrategy = 'git_blame'`.
   * `<owner>/<name>` must match a repo the installed Rynora GitHub
   * App has access to (we reuse the existing `github_installations`
   * row, no new auth flow).
   */
  blameRepoOwner: z.string().default(''),
  blameRepoName: z.string().default(''),
  /**
   * Per-org budget for git-blame lookups (one Octokit `listCommits`
   * call per failed-run assignee resolution). Defaults conservatively
   * since the GitHub Apps REST API rate-limit is 5000/hr/installation
   * and we don't want this feature to starve other surfaces (PR
   * preview runs, check runs) that share the same budget.
   */
  blameLookupsPerHour: z.number().int().min(0).max(500).default(30),
  deduplicationWindowHours: z
    .number()
    .int()
    .min(0)
    .max(24 * 30)
    .default(168),
  autoCloseOnGreenStreak: z.boolean().default(false),
  greenStreakLength: z.number().int().min(1).max(10).default(2),
  labelPrefix: z.string().default('rynora'),
  /** Per-org request budget for the hook (1-hour token bucket). */
  rateLimitPerHour: z.number().int().min(1).max(2000).default(50),

  /**
   * `pending-deeper-jira` v2 — per-project mandatory custom fields.
   *
   * Keyed by Jira project key, then by custom-field id
   * (`customfield_10100` etc.). The value is forwarded verbatim to
   * Jira's create-issue body, so it must match the field's expected
   * shape (`{ value: 'High' }` for select lists, raw strings for
   * text fields, numbers for number fields, etc.).
   *
   * Discoverable via `GET /api/admin/jira/createmeta?projectKey=…`
   * which walks `/rest/api/3/issue/createmeta` on the connected site
   * and returns the mandatory custom fields for the project so the
   * admin knows which ids to populate here.
   *
   * Empty object means "the deeper hook posts no project-specific
   * custom fields" — same behaviour as v1.1.
   */
  projectCustomFields: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),

  /**
   * `pending-deeper-jira` v2 — opt-in HTML→PDF report attachment.
   *
   * When ON the deeper hook attaches the run's full HTML report
   * rendered to PDF (via the existing `report-pdf-service` lazy
   * Puppeteer wiring; ~3-5s cold-start, sub-second when cached on
   * disk). Off by default because Puppeteer is an optional dep —
   * deployments that skipped the Chromium install will see a
   * one-line `report-pdf-renderer-unavailable` log and the
   * attachment is silently skipped (screenshots + trace zip still
   * attach).
   */
  attachReportPdf: z.boolean().default(false),

  /**
   * `pending-deeper-jira` v2 — two-way sync verbosity.
   *
   * When ON the inbound webhook handler emits per-status-transition
   * + per-assignee-change audit rows (`JIRA_ISSUE_STATUS_CHANGED`,
   * `JIRA_ISSUE_ASSIGNED`, `JIRA_ISSUE_UNASSIGNED`) in addition to
   * the v1.1 `JIRA_ISSUE_CLOSED`. Off by default because audit-log
   * write volume can ~10x for chatty Jira projects; flip ON only
   * when you need the activity trail (regulated / compliance use
   * cases). The link-row columns (`assignee_account_id`,
   * `current_status_category`, `last_jira_activity_at`) are still
   * updated regardless of this flag.
   */
  twoWaySyncAuditVerbose: z.boolean().default(false),
  /**
   * `pending-pm-tcm-tier-b-c` — preview-before-create UX gate.
   *
   * When TRUE (default — backwards compatible), the deeper-Jira hook
   * fires the create immediately on failure. When FALSE the hook
   * drafts the bug into the `bug_drafts` table instead; the
   * notification fan-out tells reviewers to open the draft in
   * Rynora's run-page, audit the preview, and click "Create Jira
   * issue" to file it. The same flag governs every PM provider.
   */
  autoCreate: z.boolean().default(true),
});
export type JiraConfig = z.infer<typeof JiraConfigSchema>;

/* ============================================================== */
/*    pending-github-issues-integration — admin-tunable hook        */
/*    config. Mirrors `JiraConfigSchema` shape.                     */
/* ============================================================== */

export const GitHubIssuesAssigneeStrategyAdminSchema = z.enum(['none', 'repo_owner', 'flow_owner_scim', 'git_blame']);

export const GitHubIssuesConfigSchema = z.object({
  enabled: z.boolean().default(false),
  /** Default repo `<owner>/<name>` issues are filed against when a
   * flow doesn't carry a more specific `repo:` tag. */
  defaultRepoOwner: z.string().default(''),
  defaultRepoName: z.string().default(''),
  /** Labels prepended to every auto-filed issue. The flow's tags
   * are appended (prefixed with `labelPrefix`-). */
  defaultLabels: z.array(z.string()).default([]),
  /**
   * Severity → milestone-title map. GitHub doesn't have priorities;
   * milestones are the closest equivalent. The hook resolves the
   * milestone number lazily by looking it up via REST on first use
   * per repo. Empty values skip the milestone field.
   */
  milestoneMap: z.record(z.string(), z.string()).default({ critical: '', major: '', minor: '', trivial: '' }),
  defaultAssigneeStrategy: GitHubIssuesAssigneeStrategyAdminSchema.default('none'),
  /** Reuse the deeper-Jira blame repo + budget for git-blame
   * resolution. Independent of the Jira config so an org can enable
   * blame on GitHub Issues without also flipping it on Jira. */
  blameRepoOwner: z.string().default(''),
  blameRepoName: z.string().default(''),
  blameLookupsPerHour: z.number().int().min(0).max(500).default(30),
  /** Dedup window — within this many hours, a re-failure on the
   * same flow comments on the existing open issue instead of
   * opening a fresh one. */
  deduplicationWindowHours: z
    .number()
    .int()
    .min(0)
    .max(24 * 30)
    .default(168),
  autoCloseOnGreenStreak: z.boolean().default(false),
  greenStreakLength: z.number().int().min(1).max(10).default(2),
  labelPrefix: z.string().default('rynora'),
  /** Per-org request budget (1-hour token bucket). */
  rateLimitPerHour: z.number().int().min(1).max(2000).default(50),
  /** Mirror of jira.twoWaySyncAuditVerbose. */
  twoWaySyncAuditVerbose: z.boolean().default(false),
  /** `pending-pm-tcm-tier-b-c` — preview-before-create gate (see
   * `jira.autoCreate` doc-comment). */
  autoCreate: z.boolean().default(true),
});
export type GitHubIssuesAdminConfig = z.infer<typeof GitHubIssuesConfigSchema>;

/* ============================================================== */
/*  pending-linear-integration — admin-tunable hook config.         */
/* ============================================================== */

export const LinearAssigneeStrategyAdminSchema = z.enum(['none', 'team_owner', 'flow_owner_scim', 'git_blame']);

export const LinearConfigSchema = z.object({
  enabled: z.boolean().default(false),
  /** Linear team id (e.g. `8a1c…`). Required when `enabled=true`. */
  defaultTeamId: z.string().default(''),
  /** Optional project id for finer routing. */
  defaultProjectId: z.string().default(''),
  /** Default state-id for opened issues; empty falls back to the
   * team's first `unstarted` state. */
  defaultStateId: z.string().default(''),
  /** Severity → Linear priority NUMBER (0=None,1=Urgent,2=High,
   * 3=Medium,4=Low). Stored as a string for admin-config uniformity. */
  priorityMap: z.record(z.string(), z.string()).default({ critical: '1', major: '2', minor: '3', trivial: '4' }),
  labelPrefix: z.string().default('rynora'),
  defaultAssigneeStrategy: LinearAssigneeStrategyAdminSchema.default('none'),
  /** Reuses the deeper-Jira blame primitive; independent budget. */
  blameRepoOwner: z.string().default(''),
  blameRepoName: z.string().default(''),
  blameLookupsPerHour: z.number().int().min(0).max(500).default(30),
  deduplicationWindowHours: z
    .number()
    .int()
    .min(0)
    .max(24 * 30)
    .default(168),
  autoCloseOnGreenStreak: z.boolean().default(false),
  greenStreakLength: z.number().int().min(1).max(10).default(2),
  rateLimitPerHour: z.number().int().min(1).max(2000).default(50),
  twoWaySyncAuditVerbose: z.boolean().default(false),
  /** `pending-pm-tcm-tier-b-c` — preview-before-create gate. */
  autoCreate: z.boolean().default(true),
});
export type LinearAdminConfig = z.infer<typeof LinearConfigSchema>;

/* ============================================================== */
/*  pending-asana-integration — admin-tunable hook config.          */
/* ============================================================== */

export const AsanaAssigneeStrategyAdminSchema = z.enum(['none', 'project_owner', 'flow_owner_scim', 'git_blame']);

export const AsanaConfigSchema = z.object({
  enabled: z.boolean().default(false),
  /** Asana project gid the hook writes to. Required when `enabled=true`. */
  defaultProjectGid: z.string().default(''),
  /** Optional section gid inside the project (e.g. "Triage", "Bugs"). */
  defaultSectionGid: z.string().default(''),
  /** Severity → Asana priority custom-field value name (Asana doesn't
   * have a built-in priority enum like Linear; the hook writes this as
   * a "Priority" custom-field value on the project. Empty = skip). */
  priorityMap: z
    .record(z.string(), z.string())
    .default({ critical: 'High', major: 'Medium', minor: 'Low', trivial: '' }),
  /** Tag prefix the hook adds (e.g. `rynora-auto`, `rynora-checkout`). */
  tagPrefix: z.string().default('rynora'),
  defaultAssigneeStrategy: AsanaAssigneeStrategyAdminSchema.default('none'),
  /** Reuses the deeper-Jira blame primitive; independent budget. */
  blameRepoOwner: z.string().default(''),
  blameRepoName: z.string().default(''),
  blameLookupsPerHour: z.number().int().min(0).max(500).default(30),
  deduplicationWindowHours: z
    .number()
    .int()
    .min(0)
    .max(24 * 30)
    .default(168),
  autoCloseOnGreenStreak: z.boolean().default(false),
  greenStreakLength: z.number().int().min(1).max(10).default(2),
  rateLimitPerHour: z.number().int().min(1).max(2000).default(50),
  twoWaySyncAuditVerbose: z.boolean().default(false),
  /** `pending-pm-tcm-tier-b-c` — preview-before-create gate. */
  autoCreate: z.boolean().default(true),
});
export type AsanaAdminConfig = z.infer<typeof AsanaConfigSchema>;

/* ============================================================== */
/*  pending-pm-tcm-tier-b-c — shared bug-draft + PM/TCM provider    */
/*  configs. New PM providers (ado_boards, jsm, clickup, monday,    */
/*  shortcut, trello, notion) and new TCM providers (testrail,      */
/*  xray, zephyr_scale, qtest, ado_testplans, practitest) all use   */
/*  a single shared schema factory so adding a new vendor is a      */
/*  one-line registry addition rather than a fresh schema.          */
/* ============================================================== */

/**
 * `pending-pm-tcm-tier-b-c` — global bug-draft slice.
 *
 * Governs the preview-before-create pipeline shared by every PM
 * provider. Drafts older than `retentionMinutes` are purged by a
 * cron worker; `notifyOnDraftCreated` fan-out into the user's
 * notification preferences.
 */
export const BugDraftConfigSchema = z.object({
  /** Master kill-switch. When OFF every provider's `autoCreate=false`
   * setting is treated as `true` (the hook falls back to v1 behaviour
   * of firing immediately). */
  enabled: z.boolean().default(true),
  /** Minutes a pending draft survives before the cron worker marks
   * it `expired` and the notification thread is closed. */
  retentionMinutes: z
    .number()
    .int()
    .min(5)
    .max(7 * 24 * 60)
    .default(60),
  /** Notify these roles when a draft is created (in addition to the
   * flow owner, who is always pinged). Empty array = flow owner only. */
  notifyRoles: z.array(z.string()).default(['admin']),
  /** Per-org rate-limit on draft creation (token bucket / hour) so a
   * misconfigured flow can't carpet-bomb the notification system. */
  rateLimitPerHour: z.number().int().min(1).max(2000).default(200),
});
export type BugDraftConfig = z.infer<typeof BugDraftConfigSchema>;

/**
 * `pending-pm-tcm-tier-b-c` — generic PM-provider config schema
 * factory. Every new bug-tracker vendor in this cluster ships its
 * admin-tunables with this exact shape so the admin-settings UI can
 * render a single reusable panel.
 */
export const PmProviderConfigSchema = z.object({
  enabled: z.boolean().default(false),
  /** OAuth / API-token redacted client credentials. Most vendors use
   * deployment-wide client_id + client_secret (NOT per-org); the
   * per-org refresh token lives in `pm_integrations`. */
  clientId: z.string().default(''),
  clientSecret: z.string().default(''),
  /** Vendor base URL override (self-hosted ADO Server, on-prem JSM,
   * etc.). Empty = vendor's public cloud endpoint. */
  baseUrl: z.string().default(''),
  /** Webhook signing secret (when the vendor supports it). */
  webhookSecret: z.string().default(''),
  /** Default vendor-side container id (ADO project, JSM service desk,
   * ClickUp list, Monday board, Trello board, Notion database id). */
  defaultProjectId: z.string().default(''),
  defaultContainerId: z.string().default(''),
  /** Severity → vendor-specific priority/value map. Free-form so
   * vendors with custom-field-based priorities (Monday, Trello,
   * Notion) can carry whatever name maps to the right value. */
  priorityMap: z.record(z.string(), z.string()).default({ critical: '', major: '', minor: '', trivial: '' }),
  /** Label / tag prefix added to every auto-filed issue. */
  labelPrefix: z.string().default('rynora'),
  /** Assignee strategy. Reuses the same vocabulary as the existing 4
   * PM integrations; `git_blame` falls through the same way (git_blame
   * → flow_owner_scim → vendor_default → none). */
  defaultAssigneeStrategy: z.enum(['none', 'vendor_default', 'flow_owner_scim', 'git_blame']).default('none'),
  blameRepoOwner: z.string().default(''),
  blameRepoName: z.string().default(''),
  blameLookupsPerHour: z.number().int().min(0).max(500).default(30),
  /** Dedup window — re-failures within this window comment on the
   * open issue instead of opening a fresh one. */
  deduplicationWindowHours: z
    .number()
    .int()
    .min(0)
    .max(24 * 30)
    .default(168),
  autoCloseOnGreenStreak: z.boolean().default(false),
  greenStreakLength: z.number().int().min(1).max(10).default(2),
  /** Per-org hook rate limit. */
  rateLimitPerHour: z.number().int().min(1).max(2000).default(50),
  twoWaySyncAuditVerbose: z.boolean().default(false),
  /** Preview-before-create gate; identical semantics to the existing
   * 4 vendors. */
  autoCreate: z.boolean().default(true),
});
export type PmProviderConfig = z.infer<typeof PmProviderConfigSchema>;

/**
 * `pending-pm-tcm-tier-b-c` — generic TCM-provider config schema
 * factory.
 */
export const TcmProviderConfigSchema = z.object({
  enabled: z.boolean().default(false),
  /** OAuth client credentials (Xray Cloud uses a separate pair; ADO
   * Test Plans reuses pm.adoBoards' creds and may leave these blank). */
  clientId: z.string().default(''),
  clientSecret: z.string().default(''),
  /** Vendor base URL (TestRail subdomain, qTest base, PractiTest
   * account url). */
  baseUrl: z.string().default(''),
  /** Default vendor container ids. For TestRail: defaultRunId. For
   * Xray: defaultTestExecutionKey. For Zephyr Scale: defaultCycleId.
   * For qTest: defaultTestCycleId. For ADO Test Plans: planId+suiteId.
   * For PractiTest: defaultProjectId+defaultRunId. */
  defaultProjectId: z.string().default(''),
  defaultSuiteId: z.string().default(''),
  defaultRunId: z.string().default(''),
  /** Rynora status → vendor verb. Defaults pre-populated for the
   * vendor's "passed" / "failed" / "blocked" / "skipped" verbs. */
  statusMap: z.record(z.string(), z.string()).default({ passed: '', failed: '', blocked: '', skipped: '', retest: '' }),
  /** Per-org push rate limit (token bucket / hour). */
  rateLimitPerHour: z.number().int().min(1).max(2000).default(200),
  /** Attach trace zip to TCM result rows in addition to screenshots.
   * Off by default for vendors that aggressively cap attachment sizes
   * (PractiTest, Zephyr Scale Cloud). */
  attachTrace: z.boolean().default(false),
  /** When the push fails, link the bug back to the TCM result so the
   * round-trip is visible from either side. Default ON. */
  linkBackToBug: z.boolean().default(true),
});
export type TcmProviderConfig = z.infer<typeof TcmProviderConfigSchema>;

export const IntegrationsSchema = z.object({
  githubAppEnabled: z.boolean(),
  jiraEnabled: z.boolean(),
  samlEnabled: z.boolean(),
  oidcEnabled: z.boolean(),
  slackEnabled: z.boolean(),
  outboundWebhooksEnabled: z.boolean(),

  /**
   * Jira OAuth credentials. Were RYNORA_JIRA_CLIENT_ID / _SECRET /
   * _REDIRECT_URI / _WEBHOOK_SECRET env vars.
   */
  jiraClientId: z.string().default(''),
  jiraClientSecret: z.string().default(''),
  jiraRedirectUri: z.string().default(''),
  jiraWebhookSecret: z.string().default(''),

  /**
   * GitHub App credentials. Were RYNORA_GITHUB_APP_ID / _PRIVATE_KEY /
   * _WEBHOOK_SECRET / _SLUG env vars.
   */
  githubAppId: z.string().default(''),
  githubAppPrivateKey: z.string().default(''),
  githubAppWebhookSecret: z.string().default(''),
  githubAppSlug: z.string().default(''),

  /**
   * Azure DevOps + Rally toggles. The actual per-org credentials live
   * in `ado_integrations` / `rally_integrations` tables (see migration
   * 037), encrypted via crypto-vault. These flags are operator-level
   * kill switches for the whole feature.
   */
  adoEnabled: z.boolean().default(false),
  rallyEnabled: z.boolean().default(false),

  /**
   * `pending-pr-preview-runs` v0.3 — GitLab + Bitbucket multi-VCS
   * preview-run delivery toggles. Per-org credentials live in
   * `gitlab_integrations` / `bitbucket_integrations` tables (migration
   * 131), encrypted via crypto-vault. These flags are operator-level
   * kill switches:
   *  - When `false`, the delivery worker rejects calls for the
   *    provider with a 503 "not configured" error, even if a per-org
   *    row exists.
   *  - When `true`, the worker honours the per-org integration row
   *    (or rejects with "no installation" if missing).
   *
   * Defaults to `false` so a Rynora deployment doesn't accidentally
   * surface the GitLab/Bitbucket comment poster before the operator
   * has provisioned a single integration row.
   */
  gitlabEnabled: z.boolean().default(false),
  bitbucketEnabled: z.boolean().default(false),
});
export type Integrations = z.infer<typeof IntegrationsSchema>;

// ---- Theme ----
export const ThemeConfigSchema = z.object({
  light: AccentBlockSchema.nullable(),
  dark: AccentBlockSchema.nullable(),
  pickedPalette: z.string().nullable(),
  pickedAt: z.string().nullable(),
  apps: AppThemeMapSchema,
  appsUpdatedAt: z.string().nullable(),
});
export type ThemeConfig = z.infer<typeof ThemeConfigSchema>;

// ---- Reporting ----
export const ReportingSchema = z.object({
  enabled: z.boolean(),
  outputDir: z.string(),
  branding: z.object({
    reportName: z.string(),
    logoUrl: z.string(),
    companyName: z.string(),
    primaryColor: z.string(),
    accentColor: z.string(),
  }),
  sections: z.array(z.string()),
  screenshots: z.object({
    captureOnFailure: z.boolean(),
    captureOnPass: z.boolean(),
    quality: z.number().int().min(1).max(100),
    format: z.string(),
  }),
  retention: z.object({
    maxReportsPerFlow: z.number().int().nonnegative(),
    maxAgeDays: z.number().int().nonnegative(),
  }),
  emailNotification: z.object({
    trigger: z.string(),
    recipients: z.array(z.string()),
    attachReport: z.boolean(),
  }),
  /**
   * Filesystem path where GDPR export bundles are written before
   * upload. Was RYNORA_EXPORT_DIR env var.
   */
  exportDir: z.string().default('/tmp/rynora-exports'),
  /**
   * Migration 092 — `pending-business-pdf-reports`. Attachment
   * format for the run-summary email. `html` (default) keeps the
   * existing self-contained HTML attachment. `pdf` switches to a
   * server-rendered PDF (heavier; slower; recipient-friendlier).
   */
  emailFormat: z.enum(['html', 'pdf']).default('html'),
  /**
   * Master kill-switch for the unauthenticated public
   * `/reports/share/<token>` dispatcher. Off → existing share-links
   * keep being listed but every public hit returns 403. Operators
   * disable this for compliance environments.
   */
  publicShareLinksEnabled: z.boolean().default(true),
  /**
   * Default expiry (hours from creation) applied when the user
   * doesn't override at share-link creation time.
   */
  reportShareLinkDefaultExpiryHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 365)
    .default(168),
  /**
   * Default per-token rate limit (req/min). Overridable per-link.
   */
  reportShareLinkDefaultRateLimitPerMin: z.number().int().min(1).max(60_000).default(60),
});
export type Reporting = z.infer<typeof ReportingSchema>;

// ---- Default environments ----
export const EnvironmentEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  baseUrl: z.string(),
  apiUrl: z.string(),
  color: z.string(),
});
export type EnvironmentEntry = z.infer<typeof EnvironmentEntrySchema>;

// ---- Storage (Backblaze B2 / local FS) ----
export const StorageSchema = z.object({
  provider: z.enum(['', 'local', 'b2']).default(''),
  b2KeyId: z.string().default(''),
  /** Encrypted via crypto-vault on write. */
  b2ApplicationKey: z.string().default(''),
  b2Bucket: z.string().default(''),
  b2Endpoint: z.string().default('s3.us-west-002.backblazeb2.com'),
  retentionDays: z.number().int().nonnegative().default(30),
});
export type Storage = z.infer<typeof StorageSchema>;

// ---- CDN (Cloudflare) ----
export const CdnWafRulesSchema = z.object({
  rateLimitRegister: z.boolean().default(true),
  botChallengeContact: z.boolean().default(true),
  blockedCountryCodes: z.array(z.string().length(2)).default([]),
});
export type CdnWafRules = z.infer<typeof CdnWafRulesSchema>;

export const CdnSchema = z.object({
  cloudflareApiToken: z.string().default(''),
  cloudflareZoneId: z.string().default(''),
  purgeOnDeploy: z.boolean().default(true),
  wafRules: CdnWafRulesSchema.default({
    rateLimitRegister: true,
    botChallengeContact: true,
    blockedCountryCodes: [],
  }),
});
export type Cdn = z.infer<typeof CdnSchema>;

// ---- Secrets manager (Bitwarden / AWS-SM, optional bootstrap helper) ----
export const SecretsManagerSchema = z.object({
  provider: z.enum(['', 'bitwarden', 'aws-sm']).default(''),
  bitwardenServerUrl: z.string().default(''),
  /** Encrypted via crypto-vault on write. */
  bitwardenAccessToken: z.string().default(''),
});
export type SecretsManager = z.infer<typeof SecretsManagerSchema>;

// ---- Cloud browser execution (Phase 5.5, distinct from runner.steelApiKey) ----
//
// `runner.steelApiKey` (above) powers the *interactive* browser feature
// (live WebRTC preview + record-and-play). This block powers cloud
// *flow execution* — when a user clicks "Run" and we dispatch the run
// to a remote Chromium instead of the operator's local Playwright.
//
// Two separate keys on purpose: an operator might run interactive on
// Steel Hobby ($0/mo, 5 concurrent, 15-min cap) and execution on
// Steel Pro ($0.05/hr, 100 concurrent, 24-hour cap), or swap one
// provider out without touching the other.
export const CloudBrowserModeSchema = z.enum(['local', 'cloud']);
export type CloudBrowserMode = z.infer<typeof CloudBrowserModeSchema>;

/**
 * Cloud browser providers Rynora knows about. Only `'steel'` has a
 * working integration as of Phase 5.5; the others are listed so the
 * admin UI can render them as future options and so a BrowserStack /
 * Sauce Labs / LambdaTest activation PR is a code-only change rather
 * than a schema migration. The route + quota layer return
 * `CLOUD_BROWSER_NOT_CONFIGURED` with reason `'provider_not_implemented'`
 * for non-Steel providers today.
 *
 * When activating a new provider:
 *   1. Drop a `<provider>-client.ts` next to `steel-client.ts`.
 *   2. Branch on `cloudCfg.provider` in `routes/cloud-browser.ts` →
 *      pick the right client factory.
 *   3. Remove the corresponding entry from `IMPLEMENTED_CLOUD_BROWSER_PROVIDERS`
 *      below so the route + quota layer stop short-circuiting.
 *   4. Update the admin UI option label (drop the "Coming soon" tag).
 */
export const CloudBrowserProviderSchema = z.enum(['steel', 'browserstack', 'saucelabs', 'lambdatest']);
export type CloudBrowserProvider = z.infer<typeof CloudBrowserProviderSchema>;

/**
 * Allowlist of providers with a wired client. Anything not in here
 * fails fast at the route layer with `provider_not_implemented`.
 *
 * IMPORTANT: keep this in sync with `routes/cloud-browser.ts` and
 * `cloud-browser-quota.ts` (which both consult it). Adding a provider
 * to the enum without adding it here is a deliberate "schema is
 * forward-compatible, code is not yet" state.
 */
export const IMPLEMENTED_CLOUD_BROWSER_PROVIDERS: readonly CloudBrowserProvider[] = [
  'steel',
  'browserstack',
  'saucelabs',
  'lambdatest',
] as const;

export const CloudBrowserSchema = z.object({
  /** Active provider. Today only `'steel'` is wired; setting this to
   *  any other value renders the UI in a "Coming soon" banner state
   *  and short-circuits run dispatch. The other enum entries exist
   *  to lock the schema shape now. */
  provider: CloudBrowserProviderSchema.default('steel'),

  // ---- Steel.dev (CDP-native, primary v1 provider) ----
  /** Steel API key. Encrypted via crypto-vault on write; redacted on read. */
  steelApiKey: z.string().default(''),
  /** Empty = official Steel API. Override only for self-hosted Steel
   *  or for routing through an enterprise outbound proxy. */
  steelEndpoint: z.string().default(''),

  // ---- BrowserStack (placeholder; not wired yet) ----
  /** BrowserStack username; empty until BrowserStack provider lands. */
  browserstackUsername: z.string().default(''),
  /** BrowserStack access key; encrypted via crypto-vault on write. */
  browserstackAccessKey: z.string().default(''),

  // ---- Sauce Labs (placeholder; not wired yet) ----
  saucelabsUsername: z.string().default(''),
  saucelabsAccessKey: z.string().default(''),
  /** Sauce data centre. Sauce splits per-region, default 'us-west-1'. */
  saucelabsRegion: z.string().default('us-west-1'),

  // ---- LambdaTest (placeholder; not wired yet) ----
  lambdatestUsername: z.string().default(''),
  lambdatestAccessKey: z.string().default(''),

  /** Master kill-switch per env. When false, every cloud-mode request
   *  short-circuits with `CLOUD_BROWSER_NOT_CONFIGURED` regardless of
   *  per-org plan or provider. */
  enabled: z.boolean().default(false),
  /** Bootstrap default is `'local'` — safe for a freshly provisioned env
   *  with no provider key. Operators flip per env in /admin/cloud-browser:
   *    Dev   = 'local'
   *    QA    = 'local' -> 'cloud' after Steel QA key is pasted
   *    UAT   = 'cloud'
   *    Prod  = 'cloud'
   *  Resolution priority: explicit request > org pref > this env default. */
  defaultMode: CloudBrowserModeSchema.default('local'),
  /** Per-org per-month overage minute ceiling. ~$1000 at $0.20/min on
   *  Pro/Starter. Hitting it 429s the run-create endpoint and pages
   *  Discord. Operator-tunable per env. */
  overageHardCapMinutesPerOrg: z.number().int().nonnegative().default(5000),

  // ---- Per-session wall-clock + idle limits (Migration 054) ----
  //
  // Cloud minutes are billed by the provider. A runaway flow that
  // never closes the session burns cash. Three layers:
  //   1. `maxSessionMinutes` is forwarded to the provider (Steel
  //      `timeout`, BS/SL/LT `idleTimeout`) so the provider auto-kills.
  //   2. The runner enforces the same wall-clock client-side (Phase 4).
  //   3. The cloud-browser-reaper worker (Phase 3) sweeps any session
  //      that the runner forgot to release.
  /** Hard wall-clock per cloud session. Max 4 hours; flows beyond
   *  this should be split. Forwarded to the provider on createSession. */
  maxSessionMinutes: z.number().int().min(5).max(240).default(60),
  /** Provider-side idle disconnect (sec). If the runner is silent for
   *  this long the provider tears the VM down. Steel uses `timeout`
   *  for both wall-clock and idle; BS/SL/LT have a dedicated knob. */
  idleSessionSeconds: z.number().int().min(30).max(3600).default(180),
  /** How often the reaper polls `cloud_browser_sessions` for stale
   *  rows (`ended_at IS NULL` AND `started_at < NOW() - maxSessionMinutes`). */
  reaperIntervalSeconds: z.number().int().min(15).max(600).default(60),
});
export type CloudBrowser = z.infer<typeof CloudBrowserSchema>;

// ---- Security (UI-side timeouts, etc.) ----
//
// Carries knobs that affect the *frontend* security posture but are
// admin-tunable per env. The two idle timeouts are surfaced through
// `/api/config/public` so the SPA can read them on first paint
// without an authenticated round-trip.
/**
 * Continuous DAST sub-config (delivery roadmap quick win 0a.3).
 *
 * Wraps the existing `scripts/security/zap-baseline.sh` so that a
 * background worker can run it on a schedule and route findings into
 * the notification router. Disabled by default — operators must opt
 * in (and pin a target URL + an on-call user) before scans start.
 */
export const ContinuousDastConfigSchema = z.object({
  /** Master switch for the nightly worker. */
  enabled: z.boolean().default(false),
  /** Hour-of-day (UTC, 0–23) at which the daily baseline scan runs.
   *  We pick a single hour rather than a full cron expression to keep
   *  the worker logic simple — pickers still cover 24 timezones. */
  hourUtc: z.number().int().min(0).max(23).default(2),
  /** URL to scan. Must be an externally-reachable origin. ZAP's
   *  baseline scanner spiders from here. */
  targetUrl: z.string().default(''),
  /** UUID of the user who should receive notifications when a scan
   *  finishes with one or more findings. Typically the security
   *  on-call rotation lead. NULL = log only, no notifications. */
  notifyUserId: z.string().nullable().default(null),
  /** When true, a single warn-level finding fails the scan record
   *  (and fires the notification). Mirrors the bash policy.json flag
   *  but is the runtime-editable copy. */
  failOnWarn: z.boolean().default(false),
  /** When true, a single fail-level finding fails the scan record. */
  failOnFail: z.boolean().default(true),
  /** When true, the worker swallows non-zero exit codes from the
   *  script (so a scan with findings doesn't crash the worker — we
   *  still record the finding row). When false, the tick records
   *  itself as errored and surfaces in `/api/admin/workers`. */
  softMode: z.boolean().default(true),
});
export type ContinuousDastConfig = z.infer<typeof ContinuousDastConfigSchema>;

export const SecurityConfigSchema = z
  .object({
    /** After this many minutes of inactivity in the flow builder we
     *  show the "You'll be signed out soon" warning modal. */
    flowBuilderIdleWarnMinutes: z.number().int().min(1).max(120).default(25),
    /** After this many minutes of inactivity we force-logout. Must be
     *  strictly greater than the warn threshold so the user has time
     *  to click "Stay signed in". */
    flowBuilderIdleLogoutMinutes: z.number().int().min(2).max(180).default(30),
    /** Continuous DAST against production (delivery roadmap 0a.3). */
    dast: ContinuousDastConfigSchema.default({
      enabled: false,
      hourUtc: 2,
      targetUrl: '',
      notifyUserId: null,
      failOnWarn: false,
      failOnFail: true,
      softMode: true,
    }),
  })
  .refine((v) => v.flowBuilderIdleLogoutMinutes > v.flowBuilderIdleWarnMinutes, {
    message: 'flowBuilderIdleLogoutMinutes must be greater than flowBuilderIdleWarnMinutes',
    path: ['flowBuilderIdleLogoutMinutes'],
  });
export type SecurityConfig = z.infer<typeof SecurityConfigSchema>;

// ---- Editor (flow-builder authoring UX) ----
//
// Operator-tunable behaviour for the flow-builder editor. Default is
// "manual save with on-close warning" because that matches the
// expectation set by every other code editor (VS Code, IntelliJ, etc.):
// changes are held in memory until the user explicitly saves, and
// closing the tab with unsaved work prompts the browser's "Leave site?"
// dialog. Operators can flip the master switch and the SPA degrades
// gracefully into a debounced background save.
//
// Surfaced via getPublicConfig() so the SPA reads it without an admin
// token. The values are not secrets — exposing them does not weaken
// any security boundary; worst case a misconfiguration causes
// background saves to fire too often or not at all.
export const EditorConfigSchema = z.object({
  /** When false, the editor never persists in the background. The
   *  user must click Save (or hit ⌘/Ctrl+S) to write to the DB.
   *  Closing the tab with unsaved work triggers the browser's
   *  beforeunload warning. Default false — matches the user's
   *  expectation from any code editor. */
  autosaveEnabled: z.boolean().default(false),
  /** Debounce after the last edit before the autosave fires. Lower
   *  than 250 ms hammers the API; higher than 10 s feels like the
   *  editor "forgot". Only consulted when `autosaveEnabled=true`. */
  autosaveDebounceMs: z.number().int().min(250).max(10_000).default(1000),
  /** When true, switching to a different flow with unsaved changes
   *  prompts the user with a Save / Discard / Cancel dialog instead
   *  of silently autosaving (or losing the changes when autosave
   *  is off). Default true so users never lose work in either mode. */
  promptOnUnsavedNavigation: z.boolean().default(true),
});
export type EditorConfig = z.infer<typeof EditorConfigSchema>;

// ---- Onboarding (Session 5 of the P1 head-to-head roadmap) ----
//
// Operator kill switches for the in-product onboarding affordances.
// Session 5 ships `firstFlowTutorial` — a 5-step interactive editor
// tour that fires once per user on first sign-in. Operators turn it
// off when:
//   - shipping a customer-facing branded build (their own onboarding
//     drives the first-flow narrative).
//   - the customer signed up via the CLI (`rynora init`) and never
//     wants the in-editor coachmarks.
//   - QA / E2E test runs (the tour overlay would intercept clicks).
//
// Default is "enabled: true" because the v0 skeleton already shipped
// to-on; flipping it off should be the rare case.
export const OnboardingFirstFlowTutorialSchema = z.object({
  /** When false, the editor never auto-launches the guided tour
   *  even for users with `onboarding_first_flow_completed = false`.
   *  The user can still trigger it manually from the help menu if a
   *  future iteration wires that in. */
  enabled: z.boolean().default(true),
});
export type OnboardingFirstFlowTutorial = z.infer<typeof OnboardingFirstFlowTutorialSchema>;

export const OnboardingConfigSchema = z.object({
  firstFlowTutorial: OnboardingFirstFlowTutorialSchema,
});
export type OnboardingConfig = z.infer<typeof OnboardingConfigSchema>;

// ---- Flow-list dashboard kill switches ----
//
// `pending-folders-to-tags-cta-resolution` v0 (Round 5 #13) — the
// dashboard's "Folders → Tags" migration CTA reads as implementation-
// language jargon to all three personas. v0 ships:
//   - The button is renamed to plain English ("Convert folders to
//     tags") so manual / business / QA users stop reading it as
//     framework jargon. (Copy lives in `@rynora/messages`.)
//   - Operators get this kill switch to hide the button entirely
//     once they've finished rolling out the universal-backfill
//     migration, without a code redeploy. v1 will flip the design
//     to silent backfill (no CTA at all).
//
// Default is `enabled: true` because the migration is still a one-
// shot user-driven action on most installs; operators with the
// universal backfill in flight can flip it off.
export const FlowListFoldersToTagsCtaSchema = z.object({
  /** When false, the FlowListPage hides the migration CTA entirely.
   *  Operators flip OFF when the universal-backfill migration is
   *  done (or in flight) and the per-project CTA would only confuse
   *  end users. Default ON for back-compat with the as-shipped UI. */
  enabled: z.boolean().default(true),
});
export type FlowListFoldersToTagsCta = z.infer<typeof FlowListFoldersToTagsCtaSchema>;

export const FlowListConfigSchema = z.object({
  foldersToTagsCta: FlowListFoldersToTagsCtaSchema.default({ enabled: true }),
});
export type FlowListConfig = z.infer<typeof FlowListConfigSchema>;

// ---- No-code program (Phase 9 onwards) ----
//
// Operator kill-switches for the no-code authoring affordances added by
// the no-code program. Each phase that ships a user-facing path in the
// flow-builder (Phase 9 = describe-mode element targeting; future phases
// will land under the same slice) should add its toggle here so operators
// can disable the surface area per env without a deploy.
//
// Defaults are "on" because each shipped phase has been QA'd and is the
// recommended path for new flows. Flipping a toggle off should be the
// rare case (LLM bill spike, customer pinning to picker-only, etc.).
export const NoCodeDescribeModeAiRefineSchema = z.object({
  /** When false, the "Refine with AI" button is hidden in the
   *  Describe form and the `/api/element-hint/refine` route returns
   *  the deterministic rule-based refinement only. */
  enabled: z.boolean().default(true),
  /** Phase 9.x — Postgres-backed refine cache TTL (days). The
   *  in-process LRU stays at 5 minutes; this knob controls how long
   *  successful LLM refines survive in the shared DB layer between
   *  API restarts. Default 14 days, range 0–365. A value of 0
   *  disables the DB cache entirely (the LRU still runs). */
  cacheTtlDays: z.number().int().min(0).max(365).default(14),
});
export type NoCodeDescribeModeAiRefine = z.infer<typeof NoCodeDescribeModeAiRefineSchema>;

export const NoCodeDescribeModeSchema = z.object({
  /** Master switch for the Describe authoring tab. When false, the
   *  3-tab picker drops to 2 tabs (Pick + Paste) — exactly the pre-
   *  Phase 9 behaviour. */
  enabled: z.boolean().default(true),
  aiRefine: NoCodeDescribeModeAiRefineSchema.default({ enabled: true, cacheTtlDays: 14 }),
  /**
   * Phase 9.x · `defer-describe-auto-promote` — when ON, a describe-
   * mode step that passes via healing has the healed selector
   * persisted onto `step.targetMeta.promotedSelector`. Subsequent
   * runs try the promoted selector FIRST and fall back to the
   * describe-ladder if it misses (no reliability regression). Off
   * by setting `enabled` to `false` — existing promotions still
   * apply at runtime, but no NEW promotions fire.
   */
  autoPromote: z.object({ enabled: z.boolean().default(true) }).default({ enabled: true }),
});
export type NoCodeDescribeMode = z.infer<typeof NoCodeDescribeModeSchema>;

// Phase 10 — natural-language step authoring. Master switch for the
// "What should this step do?" textarea + the `/api/step-parse` route.
// Defaulted to `true` because the rule-based fallback path means the
// surface works even with zero LLM credentials configured. Operators
// flip this off to fully revert the step card to the pre-Phase-10
// "tap Pick / Describe / Paste then fill three fields" UX.
export const NoCodeNaturalLanguageAuthoringSchema = z.object({
  enabled: z.boolean().default(true),
  /** Phase 10.x — Postgres-backed parse cache TTL (days). The
   *  in-process LRU stays at 5 minutes; this knob controls how long
   *  successful LLM parses survive in the shared DB layer between
   *  API restarts. Default 30 days, range 0–365. A value of 0
   *  disables the DB cache entirely (the LRU still runs). */
  cacheTtlDays: z.number().int().min(0).max(365).default(30),
});
export type NoCodeNaturalLanguageAuthoring = z.infer<typeof NoCodeNaturalLanguageAuthoringSchema>;

// Phase 11 — next-action suggestions (the chip strip below the head
// step). Three knobs:
//   - `enabled`        — master switch for the strip surface.
//   - `aiPolish.enabled` — toggles the one-call-per-scan label polish
//                          (default ON; Groq free tier).
//   - `maxElements`    — caps the runner crawl + Load-more ceiling.
//                        Default 25; clamped server-side to [1, 50].
export const NoCodeSuggestActionsAiPolishSchema = z.object({
  /** When false the polish call is skipped and chips render with
   *  rule-based labels only. The strip itself stays visible. */
  enabled: z.boolean().default(true),
  /**
   * Phase 11.4 (2026-05-14) — operator-tunable TTL for the
   * Postgres-backed polish-label cache (in days). Defaults to 7;
   * accepted range is 0–365. A value of 0 disables the DB cache
   * entirely (the in-process LRU still runs).
   *
   * Lower values make the cache more reactive to a/b-tested label
   * copy edits; higher values stretch Groq / OpenAI credit further
   * for instances that author against stable internal apps.
   */
  cacheTtlDays: z.number().int().min(0).max(365).default(7),
});
export type NoCodeSuggestActionsAiPolish = z.infer<typeof NoCodeSuggestActionsAiPolishSchema>;

/**
 * Phase 11.3 (2026-05-14) — global suggestions panel toggle.
 *
 * Defaults OFF. When operators flip it on, advanced authors see a
 * second copy of the chip strip pinned to the top of the canvas
 * (not anchored to the head step) so they don't have to scroll back
 * to the head step every time they want a fresh suggestion. The
 * panel reuses `useNextActionSuggestions`, so it shares the same
 * scan / polish / telemetry cycle as the inline strip — no
 * duplicate runner traffic and no per-panel admin-config knobs.
 */
export const NoCodeSuggestActionsGlobalPanelSchema = z.object({
  enabled: z.boolean().default(false),
});
export type NoCodeSuggestActionsGlobalPanel = z.infer<typeof NoCodeSuggestActionsGlobalPanelSchema>;

/**
 * `defer-server-side-smart-actions` (Phase 11.x) — opt-in server-
 * side classifier for smart chips. When `enabled` is `false`
 * (default) the SPA stays purely client-side. When `true`, the
 * FE calls `POST /api/suggested-actions/classify` alongside the
 * existing client builder; results merge with `dedupeById` and
 * server-classified chips override their client twins.
 *
 * `classifyCacheTtlDays` operator-tunable. The cache key is
 * `hash(projectId + outlineFingerprint)`; setting to 0 disables
 * the persistent cache (the service still answers, just always
 * re-derives).
 */
export const NoCodeSuggestActionsServerSideSchema = z.object({
  enabled: z.boolean().default(false),
});
export type NoCodeSuggestActionsServerSide = z.infer<typeof NoCodeSuggestActionsServerSideSchema>;

export const NoCodeSuggestActionsSchema = z.object({
  /** Master switch for the entire next-action chip strip. When false
   *  the FlowCanvas falls back to the pre-Phase-11 layout (just the
   *  manual `+ Insert step` button between cards). */
  enabled: z.boolean().default(true),
  aiPolish: NoCodeSuggestActionsAiPolishSchema.default({ enabled: true }),
  /** Cap on captured interactive elements per scan + on the Load-more
   *  ceiling. Clamped to [1, 50] both server- and client-side. */
  maxElements: z.number().int().positive().max(50).default(25),
  /** Phase 11.3 — opt-in global suggestions panel pinned to the top
   *  of the canvas. Defaults OFF; the inline strip stays the v1
   *  surface. */
  globalPanel: NoCodeSuggestActionsGlobalPanelSchema.default({ enabled: false }),
  /** `defer-server-side-smart-actions` — server-classified smart chips. */
  serverSide: NoCodeSuggestActionsServerSideSchema.default({ enabled: false }),
  /** `defer-server-side-smart-actions` — Postgres cache TTL for the
   *  classify endpoint. Defaults to 1 day. Setting to 0 disables
   *  the cache row write (the service still responds). */
  classifyCacheTtlDays: z.number().int().min(0).max(365).default(1),
  /**
   * Phase 11.4–11.5 — which suggestion-panel layout the editor
   * renders. Three layouts live in the codebase, switchable per env.
   *
   *   - `v1` (default) — legacy flat chip strip ("Suggested next
   *     actions" / "AI Suggestions" / "Control flow"). Unchanged
   *     behaviour, safe rollback target.
   *   - `v2` — grouped element-row layout (Phase 11.4, 2026-05-26):
   *     Smart first, then interactive elements collapsed into one
   *     row per selector (with `[Click]` / `[Type]` / `[Verify]`
   *     verb buttons) and clustered by page region (Main / Header /
   *     Footer / etc.) using the `landmark` field on the outline
   *     scan, then Control flow last. Addresses the "too many
   *     locators" complaint on dense pages but suffers from a
   *     "46-element wall" on content-heavy pages where one region
   *     dwarfs the others — kept available for rollback A/B but
   *     superseded by `v3`.
   *   - `v3` — quick-picks + browse-all modal (Phase 11.5):
   *     replaces enumeration with the top 5 context-aware Quick
   *     Picks (ranked by landmark, primary-CTA text, required
   *     fields, previous-step type, deduped against already-used
   *     selectors in the current flow) plus a single
   *     `Browse all N elements →` button that opens a searchable
   *     modal listing every captured element. Compact compared to
   *     v2's region groups; scales to pages with hundreds of
   *     elements without a wall-of-rows. Recommended default once
   *     baked.
   *
   * Ships as a per-env flag so we can canary in QA before
   * promoting to prod. Flip via the admin console under
   * No-code → Suggest actions; the change applies on next
   * editor mount.
   */
  layoutVersion: z.enum(['v1', 'v2', 'v3']).default('v3'),
  /**
   * Emits a one-shot telemetry event the first time each user mounts
   * the editor with v3 active. Default `false` post-promotion (2026-05-29
   * rollout-closed) — operators can re-enable per env for resampling.
   * The localStorage idempotency guard keeps the event silent for any
   * user who already fired it during the canary window.
   *
   * History: defaulted to `true` during the v0 canary; closed out under
   * `pending-suggestion-panel-v3-canary` promotion ship (2026-05-29).
   */
  emitCanaryTelemetry: z.boolean().default(false),
});
export type NoCodeSuggestActions = z.infer<typeof NoCodeSuggestActionsSchema>;

/**
 * Phase 8.5 (`defer-component-versioning`, 2026-05-14) — versioned
 * components knobs. `versionRetention` caps how many versions per
 * component the server keeps; older non-active versions are
 * hard-deleted by the retention sweep on every save. Defaults to 20.
 * Setting the cap below 2 disables retention sweeps (range
 * enforcement is `[2, 100]`).
 */
export const NoCodeComponentsSchema = z.object({
  versionRetention: z.number().int().min(2).max(100).default(20),
});
export type NoCodeComponents = z.infer<typeof NoCodeComponentsSchema>;

/**
 * `pending-runjs-step` (2026-05-20) — admin knobs for the inline
 * `runJS()` step (browser-context code escape hatch via Playwright's
 * `page.evaluate()`). Distinct from the server-side `script` step's
 * `getScriptingLimits(plan)` envelope — those flags govern the TS /
 * Python / Java subprocess sandbox; these govern the in-page eval.
 *
 *   - `enabled` defaults `false` on fresh installs (operators flip it
 *     on after reviewing the security posture; the SPA hides the step
 *     tile when off via the public-config flag).
 *   - `maxTimeoutMs` caps per-step wall-clock at the schema layer
 *     (default 30 s, hard ceiling 60 s). The runner clamps further to
 *     the smaller of this value and the per-step `timeout` field.
 *   - `defaultNetwork` picks the org-default for the `network` step
 *     field (`inherit` matches Cypress / Playwright defaults; flip to
 *     `blocked` if your security posture demands "no outbound traffic
 *     from a runJS step at all"). Coarse — finer allowlists are v2.
 *   - `maxCodeBytes` caps the code body size (default 32 KB, hard
 *     ceiling 128 KB) — catches "let me paste my entire bundled SPA
 *     into a step".
 *   - `bannedPatternsLint` toggles the coarse regex pre-flight that
 *     refuses to save steps containing `eval(` / `new Function(` /
 *     `Function(`. The real defence is the browser sandbox; this is
 *     a defence-in-depth that signals organisational intent.
 */
export const NoCodeRunJsSchema = z.object({
  enabled: z.boolean().default(false),
  maxTimeoutMs: z.number().int().min(1000).max(60_000).default(30_000),
  defaultNetwork: z.enum(['inherit', 'blocked']).default('inherit'),
  maxCodeBytes: z.number().int().min(1024).max(131_072).default(32_768),
  bannedPatternsLint: z.boolean().default(true),
});
export type NoCodeRunJs = z.infer<typeof NoCodeRunJsSchema>;

export const NoCodeAgenticComposerSchema = z.object({
  /** `pending-project-knowledge` v0.1 — insert-slot composer. v0 ships the flag only. */
  enabled: z.boolean().default(true),
});
export type NoCodeAgenticComposer = z.infer<typeof NoCodeAgenticComposerSchema>;

/**
 * `pending-locator-reliability` — near-miss wait before Pass 4 + LLM
 * cap + (v0.1) locator_cache knobs. Schema ships in v0 so operators
 * can flip the kill switch; cache fields are unused until hydrate/flush.
 */
export const NoCodeLocatorCacheSchema = z.object({
  enabled: z.boolean().default(true),
  ttlDays: z.number().int().min(0).max(365).default(90),
  maxEntriesPerProject: z.number().int().min(50).max(20_000).default(2000),
  failStreakEvict: z.number().int().min(1).max(20).default(3),
});
export type NoCodeLocatorCache = z.infer<typeof NoCodeLocatorCacheSchema>;

export const NoCodeLocatorReliabilitySchema = z.object({
  /** Wait remaining failure budget when Pass 1 already saw count > 0. */
  nearMissWaitBeforeLlm: z.boolean().default(true),
  /** 0 = never call LLM; 1 = default; 2 = allow tiebreaker + Pass 4. */
  maxLlmCallsPerStep: z.number().int().min(0).max(2).default(1),
  cache: NoCodeLocatorCacheSchema.default({
    enabled: true,
    ttlDays: 90,
    maxEntriesPerProject: 2000,
    failStreakEvict: 3,
  }),
});
export type NoCodeLocatorReliability = z.infer<typeof NoCodeLocatorReliabilitySchema>;

export const NoCodeConfigSchema = z.object({
  describeMode: NoCodeDescribeModeSchema.default({ enabled: true, aiRefine: { enabled: true, cacheTtlDays: 14 } }),
  naturalLanguageAuthoring: NoCodeNaturalLanguageAuthoringSchema.default({ enabled: true, cacheTtlDays: 30 }),
  suggestActions: NoCodeSuggestActionsSchema.default({
    enabled: true,
    aiPolish: { enabled: true, cacheTtlDays: 7 },
    maxElements: 25,
    globalPanel: { enabled: false },
  }),
  components: NoCodeComponentsSchema.default({ versionRetention: 20 }),
  runJs: NoCodeRunJsSchema.default({
    enabled: false,
    maxTimeoutMs: 30_000,
    defaultNetwork: 'inherit',
    maxCodeBytes: 32_768,
    bannedPatternsLint: true,
  }),
  agenticComposer: NoCodeAgenticComposerSchema.default({ enabled: true }),
  locatorReliability: NoCodeLocatorReliabilitySchema.default({
    nearMissWaitBeforeLlm: true,
    maxLlmCallsPerStep: 1,
    cache: { enabled: true, ttlDays: 90, maxEntriesPerProject: 2000, failStreakEvict: 3 },
  }),
});
export type NoCodeConfig = z.infer<typeof NoCodeConfigSchema>;

// ---- Approval workflow (Phase 4.5 + Phase 8.2) ----
//
// `runGate.enabled` is the Phase 8.2 (`defer-runbutton-gate-wiring`)
// kill-switch for the toolbar Run-button approval gate. When false
// the toolbar's `evaluateRunGate` callback is bypassed entirely and
// the run dispatches as it did pre-Phase-8.2 — exactly the existing
// behaviour. When true, the editor consults the per-project policy
// + open approval requests before dispatching the run; a gated run
// surfaces a blocking toast with the gate reason and never reaches
// the runner.
//
// Defaults OFF because the gate requires per-project policy (env
// list, approver list, requested-approver list) that hosts opt in to
// via the existing approval panel. Operators flip it on once a
// project's policy is configured.
export const ApprovalRunGateConfigSchema = z.object({
  enabled: z.boolean().default(false),
});
export type ApprovalRunGateConfig = z.infer<typeof ApprovalRunGateConfigSchema>;

/**
 * Phase 8.3 (`defer-approval-notifications`, 2026-05-14) — single-
 * channel email fan-out for approval lifecycle events. The frontend
 * still talks to the same `/api/approval-requests` endpoint; the
 * service layer detects state transitions and dispatches one
 * generic-notification email per recipient (each approver on
 * create, the requester on decide). Defaults OFF for back-compat;
 * operators flip it on once their SMTP is configured.
 */
export const ApprovalEmailNotificationsConfigSchema = z.object({
  enabled: z.boolean().default(false),
});
export type ApprovalEmailNotificationsConfig = z.infer<typeof ApprovalEmailNotificationsConfigSchema>;

export const ApprovalNotificationsConfigSchema = z.object({
  email: ApprovalEmailNotificationsConfigSchema.default({ enabled: false }),
});
export type ApprovalNotificationsConfig = z.infer<typeof ApprovalNotificationsConfigSchema>;

export const ApprovalConfigSchema = z.object({
  runGate: ApprovalRunGateConfigSchema.default({ enabled: false }),
  notifications: ApprovalNotificationsConfigSchema.default({ email: { enabled: false } }),
});
export type ApprovalConfig = z.infer<typeof ApprovalConfigSchema>;

// ---- Notifications platform (Migration 055) ----
//
// Cross-cutting admin control plane for the notification router.
// Decides: global kill switch, branding, per-channel adapter config,
// delivery rate caps, marketing kill switch, and per-category default
// channels + opt-out policy.
//
// The notification-router consults this slice as step 1 of its 6-step
// resolution pipeline (see services/notification-router.ts). Admin
// changes are picked up on the next config-cache invalidation (~60s
// or on PATCH).
export const NotificationCategoriesAdminSchema = z.object({
  transactional: z
    .object({
      defaultChannels: z.array(z.string()).default(['inapp', 'email']),
      userOptOutAllowed: z.boolean().default(false),
    })
    .default({ defaultChannels: ['inapp', 'email'], userOptOutAllowed: false }),
  account: z
    .object({
      defaultChannels: z.array(z.string()).default(['inapp', 'email']),
      userOptOutAllowed: z.boolean().default(true),
    })
    .default({ defaultChannels: ['inapp', 'email'], userOptOutAllowed: true }),
  operational: z
    .object({
      defaultChannels: z.array(z.string()).default(['inapp', 'email', 'slack']),
      userOptOutAllowed: z.boolean().default(true),
    })
    .default({ defaultChannels: ['inapp', 'email', 'slack'], userOptOutAllowed: true }),
  billing: z
    .object({
      defaultChannels: z.array(z.string()).default(['inapp', 'email']),
      userOptOutAllowed: z.boolean().default(false),
    })
    .default({ defaultChannels: ['inapp', 'email'], userOptOutAllowed: false }),
  digest: z
    .object({
      defaultChannels: z.array(z.string()).default(['inapp', 'email']),
      userOptOutAllowed: z.boolean().default(true),
    })
    .default({ defaultChannels: ['inapp', 'email'], userOptOutAllowed: true }),
  product: z
    .object({
      defaultChannels: z.array(z.string()).default(['inapp', 'email']),
      userOptOutAllowed: z.boolean().default(true),
    })
    .default({ defaultChannels: ['inapp', 'email'], userOptOutAllowed: true }),
  marketing: z
    .object({ defaultChannels: z.array(z.string()).default(['email']), userOptOutAllowed: z.boolean().default(true) })
    .default({ defaultChannels: ['email'], userOptOutAllowed: true }),
});
export type NotificationCategoriesAdmin = z.infer<typeof NotificationCategoriesAdminSchema>;

export const NotificationChannelsAdminSchema = z.object({
  email: z
    .object({
      enabled: z.boolean().default(true),
    })
    .default({ enabled: true }),
  slack: z
    .object({
      enabled: z.boolean().default(true),
      allowedHostSuffixes: z.array(z.string()).default(['hooks.slack.com']),
    })
    .default({ enabled: true, allowedHostSuffixes: ['hooks.slack.com'] }),
  teams: z
    .object({
      enabled: z.boolean().default(false),
      allowedHostSuffixes: z.array(z.string()).default(['webhook.office.com']),
    })
    .default({ enabled: false, allowedHostSuffixes: ['webhook.office.com'] }),
  discord: z
    .object({
      enabled: z.boolean().default(false),
      allowedHostSuffixes: z.array(z.string()).default(['discord.com', 'discordapp.com']),
    })
    .default({ enabled: false, allowedHostSuffixes: ['discord.com', 'discordapp.com'] }),
  webPush: z
    .object({
      enabled: z.boolean().default(false),
      /** Public VAPID key — sent to the browser on push subscribe. */
      vapidPublicKey: z.string().default(''),
      /** Private VAPID key — encrypted via crypto-vault on write. */
      vapidPrivateKey: z.string().default(''),
      vapidSubject: z.string().default('mailto:ops@rynora.app'),
    })
    .default({ enabled: false, vapidPublicKey: '', vapidPrivateKey: '', vapidSubject: 'mailto:ops@rynora.app' }),
  genericWebhook: z
    .object({
      enabled: z.boolean().default(true),
      allowedHostSuffixes: z.array(z.string()).default([]),
    })
    .default({ enabled: true, allowedHostSuffixes: [] }),
});
export type NotificationChannelsAdmin = z.infer<typeof NotificationChannelsAdminSchema>;

export const NotificationDeliveryAdminSchema = z.object({
  ratePerHourPerUser: z.number().int().nonnegative().default(60),
  ratePerHourGlobal: z.number().int().nonnegative().default(10000),
  retryAttempts: z.number().int().min(0).max(10).default(3),
  retryBackoffSeconds: z.array(z.number().int().nonnegative()).default([60, 300, 1800]),
  deadLetterAfterAttempts: z.number().int().nonnegative().default(4),
  /**
   * If set, honour the user's quiet-hours window for everything except
   * `severity = critical` (which always pages through). Operators can
   * disable per env if they need every alert in real-time.
   */
  honourQuietHours: z.boolean().default(true),
});
export type NotificationDeliveryAdmin = z.infer<typeof NotificationDeliveryAdminSchema>;

export const NotificationMarketingAdminSchema = z.object({
  enabled: z.boolean().default(false),
  senderDomain: z.string().default(''),
  /** Open + click tracking pixel/redirect for marketing sends only. */
  openTrackingEnabled: z.boolean().default(true),
  clickTrackingEnabled: z.boolean().default(true),
  requireDoubleOptIn: z.boolean().default(false),
  /** Suppression list visible to non-platform-admin org admins. */
  suppressionListVisible: z.boolean().default(false),
});
export type NotificationMarketingAdmin = z.infer<typeof NotificationMarketingAdminSchema>;

export const NotificationsAdminConfigSchema = z.object({
  enabled: z.boolean().default(true),
  defaultSenderName: z.string().default('Rynora'),
  defaultSenderEmail: z.string().default('noreply@rynora.app'),
  replyToEmail: z.string().default('hello@rynora.app'),
  physicalAddress: z.string().default(''),
  brandingLogoUrl: z.string().default(''),
  brandingPrimaryColor: z.string().default('#6366f1'),
  /** HMAC signing key for unsubscribe + tracking tokens. Encrypted via crypto-vault on write. */
  unsubscribeTokenSecret: z.string().default(''),
  channels: NotificationChannelsAdminSchema.default({} as never),
  delivery: NotificationDeliveryAdminSchema.default({} as never),
  marketing: NotificationMarketingAdminSchema.default({} as never),
  categories: NotificationCategoriesAdminSchema.default({} as never),
  /**
   * Per-template overrides keyed by templateId. Admin can override
   * subject / sender / footer without code change. `null` means
   * "use template default".
   */
  templateOverrides: z.record(EmailTemplateOverrideRefSchema()).default({}),
});

// Forward-declare a thin reference because this lives in the same file
// section; the canonical schema is in notifications.ts (admin imports
// from notifications).
function EmailTemplateOverrideRefSchema() {
  return z.object({
    subject: z.string().nullable().default(null),
    preheader: z.string().nullable().default(null),
    senderName: z.string().nullable().default(null),
    footerHtml: z.string().nullable().default(null),
  });
}
export type NotificationsAdminConfig = z.infer<typeof NotificationsAdminConfigSchema>;

// ---- Access control (per-env user allowlist) ----
export const RegistrationModeSchema = z.enum(['open', 'allowlisted', 'closed']);
export type RegistrationMode = z.infer<typeof RegistrationModeSchema>;

export const AccessConfigSchema = z.object({
  registrationMode: RegistrationModeSchema.default('open'),
  allowedEmails: z
    .object({
      local: z.array(z.string()).default([]),
      qa: z.array(z.string()).default([]),
      uat: z.array(z.string()).default([]),
      production: z.array(z.string()).default([]),
    })
    .default({ local: [], qa: [], uat: [], production: [] }),
  notifyOnReject: z.boolean().default(false),
});
export type AccessConfig = z.infer<typeof AccessConfigSchema>;

// ---- Operational state (control plane) ----
export const OpsFlagsSchema = z
  .object({
    stripeWebhookEnabled: z.boolean().default(true),
    runnerEnabled: z.boolean().default(true),
    schedulerEnabled: z.boolean().default(true),
    weeklyDigestEnabled: z.boolean().default(true),
    exportWorkerEnabled: z.boolean().default(true),
    deletionWorkerEnabled: z.boolean().default(true),
  })
  .catchall(z.boolean());
export type OpsFlags = z.infer<typeof OpsFlagsSchema>;

/** `pending-project-knowledge` — compile kill switch + caps. Compile is not an LLM call. */
export const KnowledgeConfigSchema = z.object({
  enabled: z.boolean().default(true),
  maxTokens: z.number().int().min(500).max(4000).default(2000),
  glossaryMaxTerms: z.number().int().min(10).max(200).default(50),
});
export type KnowledgeConfig = z.infer<typeof KnowledgeConfigSchema>;

/** `pending-quality-hq` v0 — Quality HQ kill switch + default project open. */
export const QualityHqFailureIntelligenceConfigSchema = z.object({
  /**
   * When true, failed Automation runs automatically create (or reuse) a
   * draft Failure Intelligence record for human review. Default OFF.
   * Never mutates Flows, reruns, or remediates.
   */
  autoCreateOnAutomationFailure: z.boolean().default(false),
});
export type QualityHqFailureIntelligenceConfig = z.infer<typeof QualityHqFailureIntelligenceConfigSchema>;

export const QualityHqConfigSchema = z.object({
  enabled: z.boolean().default(true),
  defaultProjectOpen: z.enum(['quality', 'editor']).default('quality'),
  findingsCap: z.number().int().min(20).max(200).default(100),
  uncoveredWindowDays: z.number().int().min(7).max(90).default(14),
  failureIntelligence: QualityHqFailureIntelligenceConfigSchema.default({
    autoCreateOnAutomationFailure: false,
  }),
});
export type QualityHqConfig = z.infer<typeof QualityHqConfigSchema>;

export const OpsConfigSchema = z.object({
  maintenanceMode: z.boolean().default(false),
  maintenanceMessage: z.string().default('Rynora is temporarily down for maintenance.'),
  drainTimeoutSec: z.number().int().nonnegative().default(30),
  flags: OpsFlagsSchema.default({
    stripeWebhookEnabled: true,
    runnerEnabled: true,
    schedulerEnabled: true,
    weeklyDigestEnabled: true,
    exportWorkerEnabled: true,
    deletionWorkerEnabled: true,
  }),
});
export type OpsConfig = z.infer<typeof OpsConfigSchema>;

// ---- Top-level admin config ----
export const AdminConfigSchema = z.object({
  connection: ConnectionSchema,
  server: ServerConfigSchema,
  branding: BrandingSchema,
  email: EmailSchema,
  auth: AuthConfigSchema,
  ai: AiConfigSchema,
  billing: BillingSchema,
  payments: PaymentsSchema.default({
    provider: 'stripe',
    enabled: true,
    razorpayKeyId: '',
    razorpayKeySecret: '',
    razorpayWebhookSecret: '',
    razorpayPlans: { starter: '', pro: '', team: '', enterprise: '' },
    paddleVendorId: '',
    paddleApiKey: '',
    paddleWebhookSecret: '',
    paddleEnvironment: 'sandbox',
    paddlePrices: { starter: '', pro: '', team: '', enterprise: '' },
    paypalClientId: '',
    paypalSecret: '',
    paypalWebhookId: '',
    paypalEnvironment: 'sandbox',
    paypalPlans: { starter: '', pro: '', team: '', enterprise: '' },
    lemonsqueezyStoreId: '',
    lemonsqueezyApiKey: '',
    lemonsqueezyWebhookSecret: '',
    lemonsqueezyVariants: { starter: '', pro: '', team: '', enterprise: '' },
    manualOpsEmail: '',
    manualInstructions: '',
  }),
  observability: ObservabilitySchema,
  runner: RunnerSchema,
  // Locator Strategy Overhaul · Phase 5.9 — operator-tunable Test ID
  // attribute list (the only knob in v0). Default preserves today's
  // hard-coded `['data-testid', 'data-test-id', 'data-cy']` exactly.
  locators: LocatorsConfigSchema.default({
    customTestIdAttributes: ['data-testid', 'data-test-id', 'data-cy'],
  }),
  integrations: IntegrationsSchema,
  theme: ThemeConfigSchema,
  reporting: ReportingSchema,
  security: SecurityConfigSchema.default({
    flowBuilderIdleWarnMinutes: 25,
    flowBuilderIdleLogoutMinutes: 30,
    dast: {
      enabled: false,
      hourUtc: 2,
      targetUrl: '',
      notifyUserId: null,
      failOnWarn: false,
      failOnFail: true,
      softMode: true,
    },
  }),
  editor: EditorConfigSchema.default({
    autosaveEnabled: false,
    autosaveDebounceMs: 1000,
    promptOnUnsavedNavigation: true,
  }),
  // Session 5 of the P1 head-to-head roadmap — onboarding kill switches.
  // Defaults to "enabled: true" for the editor first-flow tour because
  // the v0 skeleton has shipped to-on already.
  onboarding: OnboardingConfigSchema.default({
    firstFlowTutorial: { enabled: true },
  }),
  // `pending-folders-to-tags-cta-resolution` v0 (Round 5 #13) — flow-
  // list dashboard kill switches. Default ON so the as-shipped UI is
  // preserved; operators flip OFF once their universal migration is
  // in flight or done.
  flowList: FlowListConfigSchema.default({
    foldersToTagsCta: { enabled: true },
  }),
  defaultEnvironments: z.array(EnvironmentEntrySchema),
  storage: StorageSchema.default({
    provider: '',
    b2KeyId: '',
    b2ApplicationKey: '',
    b2Bucket: '',
    b2Endpoint: 's3.us-west-002.backblazeb2.com',
    retentionDays: 30,
  }),
  cdn: CdnSchema.default({
    cloudflareApiToken: '',
    cloudflareZoneId: '',
    purgeOnDeploy: true,
    wafRules: { rateLimitRegister: true, botChallengeContact: true, blockedCountryCodes: [] },
  }),
  secretsManager: SecretsManagerSchema.default({
    provider: '',
    bitwardenServerUrl: '',
    bitwardenAccessToken: '',
  }),
  regions: RegionsConfigSchema.default({
    currentRegion: 'us-phx',
    availableRegions: ['us-phx'],
  }),
  cloudBrowser: CloudBrowserSchema.default({
    provider: 'steel',
    steelApiKey: '',
    steelEndpoint: '',
    browserstackUsername: '',
    browserstackAccessKey: '',
    saucelabsUsername: '',
    saucelabsAccessKey: '',
    saucelabsRegion: 'us-west-1',
    lambdatestUsername: '',
    lambdatestAccessKey: '',
    enabled: false,
    defaultMode: 'local',
    overageHardCapMinutesPerOrg: 5000,
    maxSessionMinutes: 60,
    idleSessionSeconds: 180,
    reaperIntervalSeconds: 60,
  }),
  access: AccessConfigSchema.default({
    registrationMode: 'open',
    allowedEmails: { local: [], qa: [], uat: [], production: [] },
    notifyOnReject: false,
  }),
  ops: OpsConfigSchema.default({
    maintenanceMode: false,
    maintenanceMessage: 'Rynora is temporarily down for maintenance.',
    drainTimeoutSec: 30,
    flags: {
      stripeWebhookEnabled: true,
      runnerEnabled: true,
      schedulerEnabled: true,
      weeklyDigestEnabled: true,
      exportWorkerEnabled: true,
      deletionWorkerEnabled: true,
    },
  }),
  notifications: NotificationsAdminConfigSchema.default({} as never),
  noCode: NoCodeConfigSchema.default({
    describeMode: { enabled: true, aiRefine: { enabled: true, cacheTtlDays: 14 }, autoPromote: { enabled: true } },
    naturalLanguageAuthoring: { enabled: true, cacheTtlDays: 30 },
    suggestActions: {
      enabled: true,
      aiPolish: { enabled: true, cacheTtlDays: 7 },
      maxElements: 25,
      globalPanel: { enabled: false },
    },
    agenticComposer: { enabled: true },
    locatorReliability: {
      nearMissWaitBeforeLlm: true,
      maxLlmCallsPerStep: 1,
      cache: { enabled: true, ttlDays: 90, maxEntriesPerProject: 2000, failStreakEvict: 3 },
    },
  }),
  knowledge: KnowledgeConfigSchema.default({ enabled: true, maxTokens: 2000, glossaryMaxTerms: 50 }),
  qualityHq: QualityHqConfigSchema.default({
    enabled: true,
    defaultProjectOpen: 'quality',
    findingsCap: 100,
    uncoveredWindowDays: 14,
  }),
  approval: ApprovalConfigSchema.default({
    runGate: { enabled: false },
    notifications: { email: { enabled: false } },
  }),
  // Migration 086 — Contrast Advisor a11y settings (operator-tunable).
  accessibility: AccessibilityConfigSchema.default({
    contrastWcagLevel: 'AA',
    contrastCheckEnabled: true,
  }),
  // Migration 089 — Mock-server polish (`pending-postman-mock-server`).
  mockServer: MockServerConfigSchema.default({
    publicShareLinksEnabled: true,
    proxyAllowlist: [],
    defaultShareLinkRateLimitPerMin: 100,
  }),
  // Migration 094 — Deeper Jira integration (`pending-deeper-jira`).
  jira: JiraConfigSchema.default({
    enabled: false,
    componentMap: {},
    priorityMap: { critical: 'Highest', major: 'High', minor: 'Medium', trivial: 'Low' },
    autoAddToActiveSprint: false,
    epicTagPattern: '^epic-([A-Z]+-\\d+)$',
    defaultAssigneeStrategy: 'none',
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    labelPrefix: 'rynora',
    rateLimitPerHour: 50,
  }),
  // Migration 150 — pending-github-issues-integration. Off by default;
  // operator-tunable via the admin panel.
  githubIssues: GitHubIssuesConfigSchema.default({
    enabled: false,
    defaultRepoOwner: '',
    defaultRepoName: '',
    defaultLabels: [],
    milestoneMap: { critical: '', major: '', minor: '', trivial: '' },
    defaultAssigneeStrategy: 'none',
    blameRepoOwner: '',
    blameRepoName: '',
    blameLookupsPerHour: 30,
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    labelPrefix: 'rynora',
    rateLimitPerHour: 50,
    twoWaySyncAuditVerbose: false,
  }),
  // Migration 151 — pending-linear-integration. Off by default.
  linear: LinearConfigSchema.default({
    enabled: false,
    defaultTeamId: '',
    defaultProjectId: '',
    defaultStateId: '',
    priorityMap: { critical: '1', major: '2', minor: '3', trivial: '4' },
    labelPrefix: 'rynora',
    defaultAssigneeStrategy: 'none',
    blameRepoOwner: '',
    blameRepoName: '',
    blameLookupsPerHour: 30,
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    rateLimitPerHour: 50,
    twoWaySyncAuditVerbose: false,
  }),
  // Migration 152 — pending-asana-integration. Off by default.
  asana: AsanaConfigSchema.default({
    enabled: false,
    defaultProjectGid: '',
    defaultSectionGid: '',
    priorityMap: { critical: 'High', major: 'Medium', minor: 'Low', trivial: '' },
    tagPrefix: 'rynora',
    defaultAssigneeStrategy: 'none',
    blameRepoOwner: '',
    blameRepoName: '',
    blameLookupsPerHour: 30,
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    rateLimitPerHour: 50,
    twoWaySyncAuditVerbose: false,
    autoCreate: true,
  }),
  // `pending-pm-tcm-tier-b-c` — bug-draft preview-before-create
  // global slice.
  bugDraft: BugDraftConfigSchema.default({
    enabled: true,
    retentionMinutes: 60,
    notifyRoles: ['admin'],
    rateLimitPerHour: 200,
  }),
  // `pending-pm-tcm-tier-b-c` — 7 new PM providers.
  adoBoards: PmProviderConfigSchema.default({} as never),
  jsm: PmProviderConfigSchema.default({} as never),
  clickup: PmProviderConfigSchema.default({} as never),
  monday: PmProviderConfigSchema.default({} as never),
  shortcut: PmProviderConfigSchema.default({} as never),
  trello: PmProviderConfigSchema.default({} as never),
  notion: PmProviderConfigSchema.default({} as never),
  // `pending-pm-tcm-tier-b-c` — 6 TCM providers.
  testrail: TcmProviderConfigSchema.default({} as never),
  xray: TcmProviderConfigSchema.default({} as never),
  zephyrScale: TcmProviderConfigSchema.default({} as never),
  qtest: TcmProviderConfigSchema.default({} as never),
  adoTestplans: TcmProviderConfigSchema.default({} as never),
  practitest: TcmProviderConfigSchema.default({} as never),
  // Migration 097 — Long-term performance trend dashboard
  // (`pending-perf-trend-dashboard`). Default off so a fresh
  // install never writes to an unconfigured sink.
  perfTrends: PerfTrendsConfigSchema.default({
    enabled: false,
    sink: 'timescale',
    postgresMirrorEnabled: true,
    retentionDays: 365,
    readEnabled: true,
    worker: { runHourUtc: 5, lookbackHours: 24 },
  }),
  // Migration 098 — Synthetic monitoring (`pending-synthetic-monitoring`).
  // Operators opt in once they're ready to wire on-call pages; we keep
  // it off by default so a stale schedule can't accidentally page anyone.
  monitors: MonitorsAdminConfigSchema.default({
    enabled: false,
    defaultConsecutiveFailuresToPage: 2,
    defaultPageChannels: ['in_app', 'email'],
    retentionDays: 90,
    defaultChannelTargets: {},
    publicBadgesEnabled: false,
    badgeRateLimitPerMin: 60,
  }),
  // SF0 — Salesforce-preset palette toggle (`pending-salesforce-accelerators`).
  // Off by default; operators opt in once they accept the
  // "beta — accuracy not guaranteed" warning.
  salesforce: SalesforceAdminConfigSchema.default({
    presetsEnabled: false,
    defaultInstanceUrl: '',
  }),
  // Mode B (`pending-contract-testing`) — Git-backed pact fetcher token.
  contractTesting: ContractTestingAdminConfigSchema.default({
    gitToken: '',
  }),
  // V2/V3 (`pending-applitools-visual-ai`) — smart-region matching.
  visualAi: VisualAiConfigSchema.default({
    enabled: false,
    ssimThreshold: 0.97,
    windowSize: 8,
    dynamicMaskingPresets: [
      { name: 'Timestamps', selector: '.timestamp, time, [data-time]' },
      { name: 'Carousels', selector: '.carousel, [data-carousel]' },
      { name: 'Live counters', selector: '.counter[data-live]' },
    ],
  }),
  // `defer-screenshot-redaction` — PII redaction policy for screenshot
  // capture + console-log persistence. Off by default; operators opt
  // in once they've decided which selectors map to their PII surface.
  screenshotRedaction: ScreenshotRedactionConfigSchema.default({
    enabled: false,
    useDefaultSelectors: true,
    selectors: [],
    redactionStyle: 'blur',
    redactConsoleLogs: true,
  }),
  // `defer-pickers-oauth-flow` — Gmail / Outlook OAuth client
  // credentials for the in-editor "Connect <provider>" dance. Off
  // by default; the connect modal hides the button + surfaces an
  // "ask your admin" hint when `enabled === false` or the client
  // ids are still empty so half-configured installs don't ship
  // broken popups to QA users.
  providerOAuth: ProviderOAuthConfigSchema.default({
    enabled: false,
    gmailClientId: '',
    gmailClientSecret: '',
    outlookClientId: '',
    outlookClientSecret: '',
    outlookTenant: 'common',
    redirectUrl: '',
  }),
  // `defer-templates-user-saved` + `defer-templates-ai-generated`.
  // User-saved templates are on by default (no provider needed,
  // small footprint); the AI generator is opt-in so an operator
  // has to flip a switch after configuring a provider.
  flowTemplates: FlowTemplatesConfigSchema.default({
    enabled: true,
    generatorEnabled: false,
    maxPerOrg: 200,
    generatorProvider: '',
  }),
  // `pending-rynora-agent` (migration 114). Off by default — operators
  // populate `projects.crawl_root_urls` first, then flip `enabled` to
  // start the nightly discovery loop. See agent-service.ts and
  // rynora-agent-worker.ts for the consumer side.
  agent: AgentConfigSchema.default({
    enabled: false,
    runHourUtc: 3,
    maxDraftsPerDay: 5,
    reviewDeeplinkFragment: '#/agent/findings',
    slack: { enabled: false, signingSecret: '', botToken: '', defaultChannel: '' },
    exploratory: {
      enabled: false,
      runHourUtc: 2,
      wallClockMinutes: 30,
      maxFindingsPerDay: 20,
      axeProfile: 'wcag2aa',
      ignoreSelectors: [],
      formDefaults: {
        email: 'dummy@test.example',
        tel: '+15555550100',
        number: '1',
        text: 'Rynora exploratory tester',
        url: 'https://example.com',
        search: 'rynora',
      },
    },
  }),
  // `pending-prod-feedback-loop` v0 (migration 115). Off by default —
  // operators paste a Sentry PAT + slugs + targetProjectId, then flip
  // `prodFeedback.sentry.enabled` to start the hourly ingest.
  // Findings land in the same `agent_findings` table as the rynora-agent
  // loop and surface in the agent's daily digest.
  prodFeedback: ProdFeedbackConfigSchema.default({
    sentry: {
      enabled: false,
      orgSlug: '',
      projectSlug: '',
      authToken: '',
      targetProjectId: '',
      maxFindingsPerDay: 50,
      baseUrl: '',
    },
  }),
  releaseReadiness: ReleaseReadinessConfigSchema.default({
    enabled: true,
    cacheTtlHours: 24,
    shareLinkEnabled: true,
    maxLlmInputTiles: 10,
  }),
  flakeDetective: FlakeDetectiveConfigSchema.default({
    enabled: false,
    minFlakeRateToDiagnose: 0.1,
    dedupWindowDays: 7,
    traceArtifactFallback: {
      enabled: false,
      maxEntryBytes: 5_000_000,
      maxTotalBytes: 50_000_000,
      parseTimeoutMs: 3_000,
      maxNetworkRequests: 200,
      maxConsoleMessages: 200,
      maxDomMutations: 200,
    },
  }),
  // `pending-revenue-weighted-coverage` v0.1 — operator-tunable
  // per-tier weights for the revenue-weighted coverage tile. Default
  // ratios mirror locked decision §2.
  coverage: CoverageConfigSchema.default({
    revenueTierWeights: { high: 1.0, med: 0.5, low: 0.1 },
  }),
  // `pending-localization-smoke` v0.1 — locale fan-out matrix knobs.
  // Off by default; operator opts in. The 12-locale recommended
  // default + the 24-cap mirror the v0 design doc locked decisions.
  localization: LocalizationConfigSchema.default({
    enabled: false,
    defaultLocales: [
      'en-US',
      'en-GB',
      'es-ES',
      'es-MX',
      'fr-FR',
      'de-DE',
      'it-IT',
      'pt-BR',
      'ja-JP',
      'ko-KR',
      'zh-CN',
      'ar-EG',
    ],
    defaultPseudoMode: 'both',
    maxLocales: 24,
  }),
  // `pending-design-aware-visual-checks` v0.1 — Figma PAT resolver
  // knobs. Off by default. Operator pastes a PAT once (encrypted at
  // rest), threshold + cacheSeconds tunable. The encrypted PAT is
  // never echoed by `getPublicConfig`; only `hasFigmaToken` flag.
  designAware: DesignAwareConfigSchema.default({
    enabled: false,
    figmaToken: '',
    similarityThreshold: 0.95,
    cacheSeconds: 86400,
  }),
  // `pending-privacy-compliance-scanner` v0.1 — kill switch + profile
  // + body-capture mode + custom matchers. Off by default — privacy
  // features that fire by default surprise auditors. Locked-decision
  // §4 default profile = `gdpr` once enabled. Custom matchers are
  // stored here (the regexes are admin-only — the SPA public config
  // only exposes `customMatcherCount`).
  privacy: PrivacyConfigSchema.default({
    enabled: false,
    scanProfile: 'gdpr',
    captureBodies: 'never',
    customMatchers: [],
  }),

  // pending-test-ownership-oncall v0.1 (Ship 54) — accountability /
  // escalation knobs that pair with the `test_cases.owner_user_id`
  // column shipped in Ship 43. The escalation worker is OFF by
  // default; orgs opt in once enough flows have owners.
  ownership: OwnershipConfigSchema.default({
    escalation: {
      enabled: false,
      consecutiveFailureDays: 2,
    },
  }),
});
export type AdminConfig = z.infer<typeof AdminConfigSchema>;

/**
 * Cache metadata appended to GET /api/admin/config responses.
 */
export const ConfigCacheMetaSchema = z.object({
  cached: z.boolean(),
  ttlMs: z.number().int().nonnegative(),
  cachedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
});
export type ConfigCacheMeta = z.infer<typeof ConfigCacheMetaSchema>;

export const AdminConfigResponseSchema = AdminConfigSchema.extend({
  _cache: ConfigCacheMetaSchema.optional(),
});
export type AdminConfigResponse = z.infer<typeof AdminConfigResponseSchema>;

/**
 * The single source of default values. The API merges this with the
 * DB row on every read so newly added fields get sensible defaults
 * without a migration. The admin-settings UI uses it to know what an
 * empty/never-set field "looks like".
 */
export const DEFAULT_ADMIN_CONFIG: AdminConfig = {
  connection: {
    apiBaseUrl: 'http://localhost:3001',
    wsUrl: 'ws://localhost:4400',
    healthCheckTimeoutMs: 2000,
    wsReconnectMaxDelayMs: 30000,
  },
  server: {
    apiPort: 3001,
    corsOrigin: 'http://localhost:5173',
    rateLimitMax: 100,
    rateLimitWindowMs: 60000,
    jwtExpiry: '7d',
    logLevel: 'debug',
    logSampleRate: 0,
    routeLogLevels: {},
    logLevelExpiresAt: null,
    logBufferSize: 5000,
    runnerPort: 4400,
    screenshotDir: '/tmp/rynora-screenshots',
  },
  branding: {
    supportEmail: 'support@rynora.com',
    marketingUrl: '',
  },
  email: {
    provider: 'smtp',
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: false,
    smtpUser: '',
    smtpPass: '',
    smtpFrom: 'Rynora <noreply@rynora.app>',
    contactEmail: 'hello@rynora.app',
    resendApiKey: '',
    resendFromDomain: '',
  },
  auth: {
    appUrl: 'http://localhost:3100',
    jwtSecret: '',
    jwtExpiresIn: '7d',
    googleClientId: '',
    googleClientSecret: '',
    microsoftClientId: '',
    microsoftClientSecret: '',
    microsoftTenant: 'common',
    githubClientId: '',
    githubClientSecret: '',
    resetTokenExpiresHours: 1,
    googleEnabled: false,
    githubEnabled: false,
    microsoftEnabled: false,
    ipHashSalt: '',
    rateLimitAuthMax: 20,
    requireEmailVerification: true,
  },
  ai: {
    intentProvider: 'mock',
    explainProvider: '',
    healingProvider: '',
    debugProvider: '',
    rcaProvider: '',
    suggestActionsProvider: '',
    a11yFixProvider: '',
    toolUseEnabled: false,
    toolUseProvider: '',
    visionEnabled: false,
    visionProvider: '',
    tagSuggestEnabled: false,
    tagSuggestProvider: '',
    scriptAuthorProvider: '',
    agentProvider: '',
    prodErrorProvider: '',
    releaseVerdictProvider: '',
    qualityRiskProvider: '',
    flakeHypothesisProvider: '',
    locatorTiebreakerProvider: '',
    scenarioStepsProvider: '',
    scenarioDraftsProvider: '',
    failureAnalysisProvider: '',
    knowledgeAnalyzeProvider: 'groq',
    requirementIntelligenceProvider: '',
    changeImpactProvider: '',
    openaiApiKey: '',
    openaiModel: 'gpt-4o-mini',
    openaiBaseUrl: '',
    groqApiKey: '',
    groqModel: 'openai/gpt-oss-20b',
    anthropicApiKey: '',
    anthropicModel: 'claude-3-5-sonnet-20241022',
    anthropicBaseUrl: '',
    ollamaApiKey: '',
    ollamaModel: 'llama3.1:8b',
    ollamaBaseUrl: '',
    vllmApiKey: '',
    vllmModel: 'meta-llama/Meta-Llama-3.1-8B-Instruct',
    vllmBaseUrl: '',
    orgByokEnabled: true,
    streamingEnabled: true,
    // Phase 8.20 — SOAK default ON; operators flip OFF after traffic audit.
    intentPhase5OrphansEnabled: true,
  },
  billing: {
    stripePublishableKey: '',
    stripeSecretKey: '',
    stripeWebhookSecret: '',
    stripePrices: { starter: '', pro: '', team: '', enterprise: '' },
    encryptionKey: '',
  },
  payments: {
    provider: 'stripe',
    enabled: true,
    razorpayKeyId: '',
    razorpayKeySecret: '',
    razorpayWebhookSecret: '',
    razorpayPlans: { starter: '', pro: '', team: '', enterprise: '' },
    paddleVendorId: '',
    paddleApiKey: '',
    paddleWebhookSecret: '',
    paddleEnvironment: 'sandbox',
    paddlePrices: { starter: '', pro: '', team: '', enterprise: '' },
    paypalClientId: '',
    paypalSecret: '',
    paypalWebhookId: '',
    paypalEnvironment: 'sandbox',
    paypalPlans: { starter: '', pro: '', team: '', enterprise: '' },
    lemonsqueezyStoreId: '',
    lemonsqueezyApiKey: '',
    lemonsqueezyWebhookSecret: '',
    lemonsqueezyVariants: { starter: '', pro: '', team: '', enterprise: '' },
    manualOpsEmail: '',
    manualInstructions: '',
  },
  observability: {
    sentryDsnApi: '',
    sentryDsnFrontend: '',
    sentryEnvironment: '',
    sentrySampleRate: 1.0,
    sentryTracesSampleRate: 0.1,
    betterstackLogsToken: '',
    discordWebhookUrl: '',
    discordCriticalWebhookUrl: '',
    weeklyDigestDow: 1,
    weeklyDigestHourUtc: 9,
    grafanaCloudUrl: '',
    grafanaServiceAccountToken: '',
    grafanaPrometheusUsername: '',
    prometheusScrapeEnabled: true,
    healNarrative: true,
    firstSeenWebhookUrl: '',
    firstSeenWebhookSecret: '',
    aiRemediationEnabled: false,
    aiRemediationDailyQuotaPerOrg: 20,
  },
  runner: {
    browserTier: '',
    steelApiKey: '',
    maxConcurrentSessions: 2,
    atCapacityPolicy: 'reject',
    defaultViewport: { width: 1280, height: 720 },
    traceDir: '/tmp/rynora-traces',
    traceEnabled: true,
    traceRetentionDays: 30,
    wsAllowedOrigins: '',
  },
  // Locator Strategy Overhaul · Phase 5.9 — operator-tunable Test ID
  // attribute list. Default preserves the resolver's hard-coded
  // behaviour. Note: when this slice is omitted from a payload the
  // schema back-fills via `LocatorsConfigSchema.default(...)`; we mirror
  // the value here so static consumers of `DEFAULT_ADMIN_CONFIG` (tests,
  // seeders) see the canonical default without going through the parser.
  locators: {
    customTestIdAttributes: ['data-testid', 'data-test-id', 'data-cy'],
  },
  integrations: {
    githubAppEnabled: false,
    jiraEnabled: false,
    samlEnabled: false,
    oidcEnabled: false,
    slackEnabled: false,
    outboundWebhooksEnabled: true,
    jiraClientId: '',
    jiraClientSecret: '',
    jiraRedirectUri: '',
    jiraWebhookSecret: '',
    githubAppId: '',
    githubAppPrivateKey: '',
    githubAppWebhookSecret: '',
    githubAppSlug: '',
    adoEnabled: false,
    rallyEnabled: false,
    gitlabEnabled: false,
    bitbucketEnabled: false,
  },
  theme: {
    light: null,
    dark: null,
    pickedPalette: null,
    pickedAt: null,
    apps: { ...DEFAULT_APP_THEMES },
    appsUpdatedAt: null,
  },
  reporting: {
    enabled: true,
    outputDir: '/var/lib/rynora/reports',
    branding: {
      reportName: 'Automation Results',
      logoUrl: '/api/public/rynora-logo.png',
      companyName: 'Rynora',
      primaryColor: '#60a5fa',
      accentColor: '#3b82f6',
    },
    sections: ['summary', 'steps', 'healing'],
    screenshots: {
      captureOnFailure: true,
      captureOnPass: false,
      quality: 70,
      format: 'jpeg',
    },
    retention: { maxReportsPerFlow: 5, maxAgeDays: 30 },
    emailNotification: { trigger: 'failure', recipients: [], attachReport: true },
    emailFormat: 'html',
    exportDir: '/tmp/rynora-exports',
    publicShareLinksEnabled: true,
    reportShareLinkDefaultExpiryHours: 168,
    reportShareLinkDefaultRateLimitPerMin: 60,
  },
  defaultEnvironments: [
    { id: 'qa', name: 'QA', baseUrl: 'https://qa.rynora.app', apiUrl: 'https://qa.rynora.app/api', color: '#22c55e' },
    {
      id: 'uat',
      name: 'UAT',
      baseUrl: 'https://uat.rynora.app',
      apiUrl: 'https://uat.rynora.app/api',
      color: '#f59e0b',
    },
    {
      id: 'production',
      name: 'Production',
      baseUrl: 'https://rynora.app',
      apiUrl: 'https://rynora.app/api',
      color: '#ef4444',
    },
  ],
  storage: {
    provider: '',
    b2KeyId: '',
    b2ApplicationKey: '',
    b2Bucket: '',
    b2Endpoint: 's3.us-west-002.backblazeb2.com',
    retentionDays: 30,
  },
  cdn: {
    cloudflareApiToken: '',
    cloudflareZoneId: '',
    purgeOnDeploy: true,
    wafRules: {
      rateLimitRegister: true,
      botChallengeContact: true,
      blockedCountryCodes: [],
    },
  },
  secretsManager: {
    provider: '',
    bitwardenServerUrl: '',
    bitwardenAccessToken: '',
  },
  cloudBrowser: {
    provider: 'steel',
    steelApiKey: '',
    steelEndpoint: '',
    browserstackUsername: '',
    browserstackAccessKey: '',
    saucelabsUsername: '',
    saucelabsAccessKey: '',
    saucelabsRegion: 'us-west-1',
    lambdatestUsername: '',
    lambdatestAccessKey: '',
    enabled: false,
    defaultMode: 'local',
    overageHardCapMinutesPerOrg: 5000,
    maxSessionMinutes: 60,
    idleSessionSeconds: 180,
    reaperIntervalSeconds: 60,
  },
  security: {
    flowBuilderIdleWarnMinutes: 25,
    flowBuilderIdleLogoutMinutes: 30,
    dast: {
      enabled: false,
      hourUtc: 2,
      targetUrl: '',
      notifyUserId: null,
      failOnWarn: false,
      failOnFail: true,
      softMode: true,
    },
  },
  editor: {
    autosaveEnabled: false,
    autosaveDebounceMs: 1000,
    promptOnUnsavedNavigation: true,
  },
  onboarding: {
    firstFlowTutorial: { enabled: true },
  },
  flowList: {
    foldersToTagsCta: { enabled: true },
  },
  access: {
    registrationMode: 'open',
    allowedEmails: { local: [], qa: [], uat: [], production: [] },
    notifyOnReject: false,
  },
  ops: {
    maintenanceMode: false,
    maintenanceMessage: 'Rynora is temporarily down for maintenance.',
    drainTimeoutSec: 30,
    flags: {
      stripeWebhookEnabled: true,
      runnerEnabled: true,
      schedulerEnabled: true,
      weeklyDigestEnabled: true,
      exportWorkerEnabled: true,
      deletionWorkerEnabled: true,
    },
  },
  notifications: {
    enabled: true,
    defaultSenderName: 'Rynora',
    defaultSenderEmail: 'noreply@rynora.app',
    replyToEmail: 'hello@rynora.app',
    physicalAddress: '',
    brandingLogoUrl: '',
    brandingPrimaryColor: '#6366f1',
    unsubscribeTokenSecret: '',
    channels: {
      email: { enabled: true },
      slack: { enabled: true, allowedHostSuffixes: ['hooks.slack.com'] },
      teams: { enabled: false, allowedHostSuffixes: ['webhook.office.com'] },
      discord: { enabled: false, allowedHostSuffixes: ['discord.com', 'discordapp.com'] },
      webPush: { enabled: false, vapidPublicKey: '', vapidPrivateKey: '', vapidSubject: 'mailto:ops@rynora.app' },
      genericWebhook: { enabled: true, allowedHostSuffixes: [] },
    },
    delivery: {
      ratePerHourPerUser: 60,
      ratePerHourGlobal: 10000,
      retryAttempts: 3,
      retryBackoffSeconds: [60, 300, 1800],
      deadLetterAfterAttempts: 4,
      honourQuietHours: true,
    },
    marketing: {
      enabled: false,
      senderDomain: '',
      openTrackingEnabled: true,
      clickTrackingEnabled: true,
      requireDoubleOptIn: false,
      suppressionListVisible: false,
    },
    categories: {
      transactional: { defaultChannels: ['inapp', 'email'], userOptOutAllowed: false },
      account: { defaultChannels: ['inapp', 'email'], userOptOutAllowed: true },
      operational: { defaultChannels: ['inapp', 'email', 'slack'], userOptOutAllowed: true },
      billing: { defaultChannels: ['inapp', 'email'], userOptOutAllowed: false },
      digest: { defaultChannels: ['inapp', 'email'], userOptOutAllowed: true },
      product: { defaultChannels: ['inapp', 'email'], userOptOutAllowed: true },
      marketing: { defaultChannels: ['email'], userOptOutAllowed: true },
    },
    templateOverrides: {},
  },
  noCode: {
    describeMode: { enabled: true, aiRefine: { enabled: true, cacheTtlDays: 14 }, autoPromote: { enabled: true } },
    naturalLanguageAuthoring: { enabled: true, cacheTtlDays: 30 },
    suggestActions: {
      enabled: true,
      aiPolish: { enabled: true, cacheTtlDays: 7 },
      maxElements: 25,
      globalPanel: { enabled: false },
      serverSide: { enabled: false },
      classifyCacheTtlDays: 1,
      layoutVersion: 'v3',
      emitCanaryTelemetry: false,
    },
    components: { versionRetention: 20 },
    runJs: {
      enabled: false,
      maxTimeoutMs: 30_000,
      defaultNetwork: 'inherit',
      maxCodeBytes: 32_768,
      bannedPatternsLint: true,
    },
    agenticComposer: { enabled: true },
    locatorReliability: {
      nearMissWaitBeforeLlm: true,
      maxLlmCallsPerStep: 1,
      cache: { enabled: true, ttlDays: 90, maxEntriesPerProject: 2000, failStreakEvict: 3 },
    },
  },
  knowledge: { enabled: true, maxTokens: 2000, glossaryMaxTerms: 50 },
  qualityHq: {
    enabled: true,
    defaultProjectOpen: 'quality',
    findingsCap: 100,
    uncoveredWindowDays: 14,
    failureIntelligence: {
      autoCreateOnAutomationFailure: false,
    },
  },
  approval: {
    runGate: { enabled: false },
    notifications: { email: { enabled: false } },
  },
  accessibility: {
    contrastWcagLevel: 'AA',
    contrastCheckEnabled: true,
  },
  mockServer: {
    publicShareLinksEnabled: true,
    proxyAllowlist: [],
    defaultShareLinkRateLimitPerMin: 100,
  },
  jira: {
    enabled: false,
    componentMap: {},
    priorityMap: { critical: 'Highest', major: 'High', minor: 'Medium', trivial: 'Low' },
    autoAddToActiveSprint: false,
    epicTagPattern: '^epic-([A-Z]+-\\d+)$',
    defaultAssigneeStrategy: 'none',
    blameRepoOwner: '',
    blameRepoName: '',
    blameLookupsPerHour: 30,
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    labelPrefix: 'rynora',
    rateLimitPerHour: 50,
    projectCustomFields: {},
    attachReportPdf: false,
    twoWaySyncAuditVerbose: false,
    autoCreate: true,
  },
  githubIssues: {
    enabled: false,
    defaultRepoOwner: '',
    defaultRepoName: '',
    defaultLabels: [],
    milestoneMap: { critical: '', major: '', minor: '', trivial: '' },
    defaultAssigneeStrategy: 'none',
    blameRepoOwner: '',
    blameRepoName: '',
    blameLookupsPerHour: 30,
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    labelPrefix: 'rynora',
    rateLimitPerHour: 50,
    twoWaySyncAuditVerbose: false,
    autoCreate: true,
  },
  linear: {
    enabled: false,
    defaultTeamId: '',
    defaultProjectId: '',
    defaultStateId: '',
    priorityMap: { critical: '1', major: '2', minor: '3', trivial: '4' },
    labelPrefix: 'rynora',
    defaultAssigneeStrategy: 'none',
    blameRepoOwner: '',
    blameRepoName: '',
    blameLookupsPerHour: 30,
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    rateLimitPerHour: 50,
    twoWaySyncAuditVerbose: false,
    autoCreate: true,
  },
  asana: {
    enabled: false,
    defaultProjectGid: '',
    defaultSectionGid: '',
    priorityMap: { critical: 'High', major: 'Medium', minor: 'Low', trivial: '' },
    tagPrefix: 'rynora',
    defaultAssigneeStrategy: 'none',
    blameRepoOwner: '',
    blameRepoName: '',
    blameLookupsPerHour: 30,
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    rateLimitPerHour: 50,
    twoWaySyncAuditVerbose: false,
    autoCreate: true,
  },
  // `pending-pm-tcm-tier-b-c` — bug-draft preview pipeline.
  bugDraft: {
    enabled: true,
    retentionMinutes: 60,
    notifyRoles: ['admin'],
    rateLimitPerHour: 200,
  },
  // `pending-pm-tcm-tier-b-c` — 7 new PM providers default-disabled.
  adoBoards: {
    enabled: false,
    clientId: '',
    clientSecret: '',
    baseUrl: '',
    webhookSecret: '',
    defaultProjectId: '',
    defaultContainerId: '',
    priorityMap: { critical: '1', major: '2', minor: '3', trivial: '4' },
    labelPrefix: 'rynora',
    defaultAssigneeStrategy: 'none',
    blameRepoOwner: '',
    blameRepoName: '',
    blameLookupsPerHour: 30,
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    rateLimitPerHour: 50,
    twoWaySyncAuditVerbose: false,
    autoCreate: true,
  },
  jsm: {
    enabled: false,
    clientId: '',
    clientSecret: '',
    baseUrl: '',
    webhookSecret: '',
    defaultProjectId: '',
    defaultContainerId: '',
    priorityMap: { critical: 'Highest', major: 'High', minor: 'Medium', trivial: 'Low' },
    labelPrefix: 'rynora',
    defaultAssigneeStrategy: 'none',
    blameRepoOwner: '',
    blameRepoName: '',
    blameLookupsPerHour: 30,
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    rateLimitPerHour: 50,
    twoWaySyncAuditVerbose: false,
    autoCreate: true,
  },
  clickup: {
    enabled: false,
    clientId: '',
    clientSecret: '',
    baseUrl: '',
    webhookSecret: '',
    defaultProjectId: '',
    defaultContainerId: '',
    priorityMap: { critical: '1', major: '2', minor: '3', trivial: '4' },
    labelPrefix: 'rynora',
    defaultAssigneeStrategy: 'none',
    blameRepoOwner: '',
    blameRepoName: '',
    blameLookupsPerHour: 30,
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    rateLimitPerHour: 50,
    twoWaySyncAuditVerbose: false,
    autoCreate: true,
  },
  monday: {
    enabled: false,
    clientId: '',
    clientSecret: '',
    baseUrl: '',
    webhookSecret: '',
    defaultProjectId: '',
    defaultContainerId: '',
    priorityMap: { critical: 'High', major: 'Medium', minor: 'Low', trivial: '' },
    labelPrefix: 'rynora',
    defaultAssigneeStrategy: 'none',
    blameRepoOwner: '',
    blameRepoName: '',
    blameLookupsPerHour: 30,
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    rateLimitPerHour: 50,
    twoWaySyncAuditVerbose: false,
    autoCreate: true,
  },
  shortcut: {
    enabled: false,
    clientId: '',
    clientSecret: '',
    baseUrl: '',
    webhookSecret: '',
    defaultProjectId: '',
    defaultContainerId: '',
    priorityMap: { critical: 'high', major: 'medium', minor: 'low', trivial: '' },
    labelPrefix: 'rynora',
    defaultAssigneeStrategy: 'none',
    blameRepoOwner: '',
    blameRepoName: '',
    blameLookupsPerHour: 30,
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    rateLimitPerHour: 50,
    twoWaySyncAuditVerbose: false,
    autoCreate: true,
  },
  trello: {
    enabled: false,
    clientId: '',
    clientSecret: '',
    baseUrl: '',
    webhookSecret: '',
    defaultProjectId: '',
    defaultContainerId: '',
    priorityMap: { critical: 'red', major: 'orange', minor: 'yellow', trivial: '' },
    labelPrefix: 'rynora',
    defaultAssigneeStrategy: 'none',
    blameRepoOwner: '',
    blameRepoName: '',
    blameLookupsPerHour: 30,
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    rateLimitPerHour: 50,
    twoWaySyncAuditVerbose: false,
    autoCreate: true,
  },
  notion: {
    enabled: false,
    clientId: '',
    clientSecret: '',
    baseUrl: '',
    webhookSecret: '',
    defaultProjectId: '',
    defaultContainerId: '',
    priorityMap: { critical: 'High', major: 'Medium', minor: 'Low', trivial: '' },
    labelPrefix: 'rynora',
    defaultAssigneeStrategy: 'none',
    blameRepoOwner: '',
    blameRepoName: '',
    blameLookupsPerHour: 30,
    deduplicationWindowHours: 168,
    autoCloseOnGreenStreak: false,
    greenStreakLength: 2,
    rateLimitPerHour: 50,
    twoWaySyncAuditVerbose: false,
    autoCreate: true,
  },
  // `pending-pm-tcm-tier-b-c` — 6 TCM providers default-disabled.
  testrail: {
    enabled: false,
    clientId: '',
    clientSecret: '',
    baseUrl: '',
    defaultProjectId: '',
    defaultSuiteId: '',
    defaultRunId: '',
    statusMap: { passed: '1', failed: '5', blocked: '2', skipped: '3', retest: '4' },
    rateLimitPerHour: 200,
    attachTrace: false,
    linkBackToBug: true,
  },
  xray: {
    enabled: false,
    clientId: '',
    clientSecret: '',
    baseUrl: '',
    defaultProjectId: '',
    defaultSuiteId: '',
    defaultRunId: '',
    statusMap: { passed: 'PASSED', failed: 'FAILED', blocked: 'BLOCKED', skipped: 'TODO', retest: 'EXECUTING' },
    rateLimitPerHour: 200,
    attachTrace: false,
    linkBackToBug: true,
  },
  zephyrScale: {
    enabled: false,
    clientId: '',
    clientSecret: '',
    baseUrl: '',
    defaultProjectId: '',
    defaultSuiteId: '',
    defaultRunId: '',
    statusMap: { passed: 'Pass', failed: 'Fail', blocked: 'Blocked', skipped: 'Not Executed', retest: 'In Progress' },
    rateLimitPerHour: 200,
    attachTrace: false,
    linkBackToBug: true,
  },
  qtest: {
    enabled: false,
    clientId: '',
    clientSecret: '',
    baseUrl: '',
    defaultProjectId: '',
    defaultSuiteId: '',
    defaultRunId: '',
    statusMap: { passed: 'PASS', failed: 'FAIL', blocked: 'BLOCKED', skipped: 'SKIP', retest: 'INCOMPLETE' },
    rateLimitPerHour: 200,
    attachTrace: false,
    linkBackToBug: true,
  },
  adoTestplans: {
    enabled: false,
    clientId: '',
    clientSecret: '',
    baseUrl: '',
    defaultProjectId: '',
    defaultSuiteId: '',
    defaultRunId: '',
    statusMap: {
      passed: 'Passed',
      failed: 'Failed',
      blocked: 'Blocked',
      skipped: 'NotExecuted',
      retest: 'NotApplicable',
    },
    rateLimitPerHour: 200,
    attachTrace: false,
    linkBackToBug: true,
  },
  practitest: {
    enabled: false,
    clientId: '',
    clientSecret: '',
    baseUrl: '',
    defaultProjectId: '',
    defaultSuiteId: '',
    defaultRunId: '',
    statusMap: { passed: 'PASSED', failed: 'FAILED', blocked: 'BLOCKED', skipped: 'N/A', retest: 'RETEST' },
    rateLimitPerHour: 200,
    attachTrace: false,
    linkBackToBug: true,
  },
  perfTrends: {
    enabled: false,
    sink: 'timescale',
    postgresMirrorEnabled: true,
    retentionDays: 365,
    readEnabled: true,
    worker: { runHourUtc: 5, lookbackHours: 24 },
  },
  monitors: {
    enabled: false,
    defaultConsecutiveFailuresToPage: 2,
    defaultPageChannels: ['in_app', 'email'],
    retentionDays: 90,
    defaultChannelTargets: {},
    publicBadgesEnabled: false,
    badgeRateLimitPerMin: 60,
  },
  salesforce: {
    presetsEnabled: false,
    defaultInstanceUrl: '',
  },
  contractTesting: {
    gitToken: '',
  },
  visualAi: {
    enabled: false,
    ssimThreshold: 0.97,
    windowSize: 8,
    dynamicMaskingPresets: [
      { name: 'Timestamps', selector: '.timestamp, time, [data-time]' },
      { name: 'Carousels', selector: '.carousel, [data-carousel]' },
      { name: 'Live counters', selector: '.counter[data-live]' },
    ],
    matrix: {
      enabled: false,
      defaultCells: [],
      maxCells: 8,
    },
  },
  screenshotRedaction: {
    enabled: false,
    useDefaultSelectors: true,
    selectors: [],
    redactionStyle: 'blur',
    redactConsoleLogs: true,
  },
  providerOAuth: {
    enabled: false,
    gmailClientId: '',
    gmailClientSecret: '',
    outlookClientId: '',
    outlookClientSecret: '',
    outlookTenant: 'common',
    redirectUrl: '',
  },
  flowTemplates: {
    enabled: true,
    generatorEnabled: false,
    maxPerOrg: 200,
    generatorProvider: '',
  },
  agent: {
    enabled: false,
    runHourUtc: 3,
    maxDraftsPerDay: 5,
    reviewDeeplinkFragment: '#/agent/findings',
    slack: { enabled: false, signingSecret: '', botToken: '', defaultChannel: '' },
    exploratory: {
      enabled: false,
      runHourUtc: 2,
      wallClockMinutes: 30,
      maxFindingsPerDay: 20,
      axeProfile: 'wcag2aa',
      ignoreSelectors: [],
      formDefaults: {
        email: 'dummy@test.example',
        tel: '+15555550100',
        number: '1',
        text: 'Rynora exploratory tester',
        url: 'https://example.com',
        search: 'rynora',
      },
    },
  },
  prodFeedback: {
    sentry: {
      enabled: false,
      orgSlug: '',
      projectSlug: '',
      authToken: '',
      targetProjectId: '',
      maxFindingsPerDay: 50,
      baseUrl: '',
    },
  },
  releaseReadiness: {
    enabled: true,
    cacheTtlHours: 24,
    shareLinkEnabled: true,
    maxLlmInputTiles: 10,
  },
  flakeDetective: {
    enabled: false,
    minFlakeRateToDiagnose: 0.1,
    dedupWindowDays: 7,
    traceArtifactFallback: {
      enabled: false,
      maxEntryBytes: 5_000_000,
      maxTotalBytes: 50_000_000,
      parseTimeoutMs: 3_000,
      maxNetworkRequests: 200,
      maxConsoleMessages: 200,
      maxDomMutations: 200,
    },
  },
  coverage: {
    revenueTierWeights: { high: 1.0, med: 0.5, low: 0.1 },
    dollarValuePerTier: { high: 0, med: 0, low: 0 },
    currency: 'USD',
  },
  localization: {
    enabled: false,
    defaultLocales: [
      'en-US',
      'en-GB',
      'es-ES',
      'es-MX',
      'fr-FR',
      'de-DE',
      'it-IT',
      'pt-BR',
      'ja-JP',
      'ko-KR',
      'zh-CN',
      'ar-EG',
    ],
    defaultPseudoMode: 'both',
    maxLocales: 24,
  },
  designAware: {
    enabled: false,
    figmaToken: '',
    sketchToken: '',
    penpotToken: '',
    penpotBaseUrl: 'https://design.penpot.app',
    similarityThreshold: 0.95,
    cacheSeconds: 86400,
  },
  privacy: {
    enabled: false,
    scanProfile: 'gdpr',
    captureBodies: 'never',
    customMatchers: [],
  },
  ownership: {
    escalation: {
      enabled: false,
      consecutiveFailureDays: 2,
    },
  },
  regions: {
    currentRegion: 'us-phx',
    availableRegions: ['us-phx'],
  },
};
